import emitter from "@/utils/emitter";
import apiClient, { deleteAuthToken } from "@/utils/apiClient";
import router from "@/router";

/**
 * Deletes a client and updates sessionStorage to remove the client and their locations.
 * If a route is provided, the function redirects to the specified path after deletion.
 * Displays an alert in case of error or failure.
 *
 * @async
 * @function deleteClient
 * @param {number} clientID - The ID of the client to be deleted.
 * @param {string} pathToPush - The path to redirect to after deletion, if provided.
 * @param {Object} context - The application context, used for displaying alerts.
 */
export async function deleteClient(clientID, pathToPush, context) {
    try {
        const response = await apiClient.delete(
            `/api/delete_client/${clientID}/`
        );
        if (response.data.success) {
            // Remove client from sessionStorage
            const myClients = await JSON.parse(
                sessionStorage.getItem("myClients") || "[]"
            );
            const updatedClients = myClients.filter(
                client => client.id !== clientID
            );
            await sessionStorage.setItem(
                "myClients",
                JSON.stringify(updatedClients)
            );

            // Remove client's locations from sessionStorage
            const myRecentLocations = await JSON.parse(
                sessionStorage.getItem("myRecentLocations") || "[]"
            );
            const updatedLocations = myRecentLocations.filter(
                location => location.client.id !== clientID
            );
            await sessionStorage.setItem(
                "myRecentLocations",
                JSON.stringify(updatedLocations)
            );

            emitter.emit("clientDeleted", clientID);
            if (pathToPush) {
                await pushToRoute(pathToPush);
            }
        } else {
            console.error(response.data.message);
        }
    } catch (error) {
        console.error(error);
        showAlertModal(
            context.$store,
            "A apărut o eroare la ștergerea clientului. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
    }
}

/**
 * Deletes a location and updates sessionStorage to remove the location.
 * Fetches updated location data after deletion and updates the local state.
 * Displays an alert in case of error or failure.
 *
 * @async
 * @function deleteLocation
 * @param {number} locationId - The ID of the location to be deleted.
 * @param {Object} currentClient - The current client object, which will be reset.
 * @param {Array} recentLocations - The list of recent locations, which will be updated.
 * @param {Array} location - The location data, which will be filtered.
 * @param {Object} context - The application context, used for displaying alerts.
 * @returns {Promise<Array>} - Returns the updated list of locations.
 */
export async function deleteLocation(
    context,
    locationId,
    currentClient,
    recentLocations,
    location
) {
    try {
        await apiClient.delete(`/api/delete_location/${locationId}/`);
        currentClient = null;
        // Delete the location from sessionStorage's myRecentLocations array
        const myRecentLocations =
            JSON.parse(sessionStorage.getItem("myRecentLocations")) || [];
        const locationIndex = myRecentLocations.findIndex(
            location => location.id === locationId
        );
        if (locationIndex !== -1) {
            myRecentLocations.splice(locationIndex, 1);
            sessionStorage.setItem(
                "myRecentLocations",
                JSON.stringify(myRecentLocations)
            );
        }
        await fetchData("locations", context).then(() => {
            recentLocations = JSON.parse(
                sessionStorage.getItem("myRecentLocations")
            );
        });
        if (location) {
            location = location.filter(location => location.id !== locationId);
            return location;
        } else {
            return recentLocations;
        }
    } catch (error) {
        console.error("Error deleting location:", error);
        showAlertModal(
            context.$store,
            "A apărut o eroare la ștergerea locației. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
    }
}

/**
 * Navigates to a specified route.
 * Uses the Vue router to push the user to the provided route.
 *
 * @async
 * @function pushToRoute
 * @param {string} route - The route to navigate to.
 */
export async function pushToRoute(route) {
    await router.push(route);
}

/**
 * Fetches data from various endpoints based on the specified type.
 * Updates sessionStorage with the fetched data (users, locations, or intermediaries).
 * Displays an alert in case of error or failure.
 *
 * @async
 * @function fetchData
 * @param {string} [type='all'] - The type of data to fetch ('all', 'users', 'locations', or 'intermediaries').
 * @param {Object} context - The application context, used for displaying alerts.
 */
export async function fetchData(type = "all", context) {
    if (type === "all" || type === "users") {
        await apiClient
            .get("/api/users/")
            .then(response => {
                let myUsers = response.data;
                sessionStorage.setItem("myUsers", JSON.stringify(myUsers));
            })
            .catch(error => {
                showAlertModal(
                    context.$store,
                    "A apărut o eroare la fetch-ul utilizatorilor. Vă rugăm să încercați din nou.",
                    "danger",
                    12000,
                    error
                );
                console.log(error);
            });
    }
    if (type === "all" || type === "locations") {
        await apiClient
            .get("/api/locations/last_modified/")
            .then(async response => {
                let recentLocations = await parseLocations(response.data);
                sessionStorage.setItem(
                    "myRecentLocations",
                    JSON.stringify(recentLocations)
                );
            })
            .catch(error => {
                showAlertModal(
                    context.$store,
                    "A apărut o eroare la fetch-ul locațiilor. Vă rugăm să încercați din nou.",
                    "danger",
                    12000,
                    error
                );
                console.log(error);
            });
    }
    if (type === "all" || type === "intermediaries") {
        let userId = JSON.parse(sessionStorage.getItem("userId"));
        let endpoint = "/api/intermediaries/";

        // Always include the agent_id query parameter, regardless of user role.
        if (userId !== null) {
            endpoint += `?agent_id=${userId}`;
        }

        await apiClient
            .get(endpoint)
            .then(response => {
                let intermediaries = response.data;
                sessionStorage.setItem(
                    "intermediaries",
                    JSON.stringify(intermediaries)
                );
            })
            .catch(error => {
                showAlertModal(
                    context.$store,
                    "A apărut o eroare la fetch-ul intermediarilor. Vă rugăm să încercați din nou.",
                    "danger",
                    12000,
                    error
                );
                console.log(error);
            });
    }
}

/**
 * Fetches the locations associated with a client.
 * Sends an API request to retrieve the locations for the given client ID.
 * If locations are returned, they are parsed and their total area is calculated.
 * Displays an alert in case of error or failure.
 *
 * @async
 * @function getClientLocations
 * @param {number} clientId - The ID of the client.
 * @param {Object} context - The application context, used for displaying alerts.
 * @returns {Promise<Array>} A list of locations for the client, or an empty array in case of error.
 */
export async function getClientLocations(clientId, context) {
    let locations = [];
    try {
        const locationsResponse = await apiClient.get(
            `/api/list_locations/${clientId}/`
        );
        if (
            locationsResponse.data.locations &&
            locationsResponse.data.locations.length > 0
        ) {
            locations = await parseLocations(locationsResponse.data.locations);
        }
    } catch (error) {
        if (context && context.$store) {
            showAlertModal(
                context.$store,
                "A apărut o eroare la fetch-ul locațiilor clientului. Vă rugăm să încercați din nou.",
                "danger",
                12000,
                error
            );
        }
        console.error(error);
    }
    return locations;
}

/**
 * Fetches a paginated page of locations for a given client.
 *
 * @async
 * @function getClientLocationsPage
 * @param {number} clientId - The ID of the client.
 * @param {Object} context - The application context, used for displaying alerts.
 * @param {number} limit - Number of locations to fetch.
 * @param {number} offset - Offset from which to start fetching.
 * @param {string} [search] - Optional search term to filter locations by description (backend search).
 * @returns {Promise<{locations: Array, nextOffset: (number|null)}>}
 */
export async function getClientLocationsPage(clientId, context, limit = 3, offset = 0, search = '') {
    let locations = [];
    let nextOffset = null;

    try {
        const params = { limit, offset };
        if (search && search.trim()) {
            params.search = search.trim();
        }
        const locationsResponse = await apiClient.get(
            `/api/list_locations/${clientId}/`,
            { params }
        );

        if (
            locationsResponse.data.locations &&
            locationsResponse.data.locations.length > 0
        ) {
            locations = await parseLocations(locationsResponse.data.locations);
        }

        if (typeof locationsResponse.data.next_offset === 'number') {
            nextOffset = locationsResponse.data.next_offset;
        }
    } catch (error) {
        if (context && context.$store) {
            showAlertModal(
                context.$store,
                "A apărut o eroare la fetch-ul locațiilor clientului. Vă rugăm să încercați din nou.",
                "danger",
                12000,
                error
            );
        }
        console.error(error);
    }

    return { locations, nextOffset };
}

/**
 * Parses the list of locations and calculates the total area for each location.
 * Each location's rooms are summed up to get the total area for that location.
 *
 * @async
 * @function parseLocations
 * @param {Array} locations - The list of locations to be parsed.
 * @returns {Array} The parsed locations, each with an additional `totalArea` property.
 */
export async function parseLocations(locations) {
    return locations.map(location => {
        let totalArea = 0;
        if (location.rooms && location.rooms.length > 0) {
            totalArea = location.rooms.reduce((accumulator, room) => {
                const area = parseFloat(room.area);
                return accumulator + (isNaN(area) ? 0 : area);
            }, 0);
        }
        return {
            ...location,
            totalArea,
        };
    });
}

/**
 * Saves the selected products for an offer.
 * Filters out products with invalid prices or a price of "0.00".
 * Sends an API request to create offer items with the valid selected products.
 * Displays an alert in case of error or failure.
 *
 * @async
 * @function saveSelectedProducts
 * @param {Object} context - The application context, used for displaying alerts.
 * @param {Array} selectedProducts - The list of selected products to be saved.
 */
export async function saveSelectedProducts(context, selectedProducts) {
    try {
        // Filter out products with falsy price or "0.00"
        const validProducts = selectedProducts.filter(
            product => product.price && product.price !== "0.00"
        );

        if (validProducts.length === 0) {
            showAlertModal(
                context.$store,
                "A apărut o eroare. Vă rugăm să încercați din nou.",
                "danger",
                12000
            );
            return;
        }

        const normalizeUnitValue = (value) => {
            if (value === null || value === undefined || value === "") return null;
            if (typeof value === "string" && value.startsWith("unit_")) {
                return value.slice(5);
            }
            return value;
        };

        // Ensure is_quantity_edited is always boolean and keep floor/apartment parity.
        const payload = validProducts.map((p) => ({
            ...p,
            floor:
                (p.floor ?? "") !== ""
                    ? p.floor
                    : Number(p.compartimentation ?? p.compartmentation ?? 0) === 1
                        ? normalizeUnitValue(p.unitId ?? p.unit)
                        : p.floor,
            apartment:
                (p.apartment ?? "") !== ""
                    ? p.apartment
                    : Number(p.compartimentation ?? p.compartmentation ?? 0) === 2
                        ? normalizeUnitValue(p.unitId ?? p.unit)
                        : p.apartment,
            is_quantity_edited: p.is_quantity_edited === true,
        }));

        const response = await apiClient.post("/api/create_offer_items/", {
            selected_products: payload,
        });

        if (response.status === 201) {
            console.log("All items saved successfully");
        } else {
            console.log("Error while saving items");
        }
    } catch (error) {
        console.error("Error while saving items", error);
        showAlertModal(
            context.$store,
            "A apărut o eroare. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
    }
}

/**
 * Updates the selected products for an offer.
 * Filters out products with falsy prices or price equal to "0.00".
 * Sends an API request to update the offer items.
 * Displays an alert in case of error or failure.
 *
 * @async
 * @function updateSelectedProducts
 * @param {Object} context - The application context, used for displaying alerts.
 * @param {Array} selectedProducts - The list of products selected for the offer.
 * @returns {Promise<Object|undefined>} The updated products data or undefined in case of error.
 */
export async function updateSelectedProducts(context, selectedProducts) {
    try {
        // Filter out products with falsy price or "0.00"
        const validProducts = selectedProducts.filter(
            product => product.price && product.price !== "0.00"
        );

        if (validProducts.length === 0) {
            showAlertModal(
                context.$store,
                "A apărut o eroare. Vă rugăm să încercați din nou.",
                "danger",
                12000
            );
            return;
        }

        const normalizeUnitValue = (value) => {
            if (value === null || value === undefined || value === "") return null;
            if (typeof value === "string" && value.startsWith("unit_")) {
                return value.slice(5);
            }
            return value;
        };

        // Oferte vechi: is_quantity_edited poate lipsi sau fi null
        const payload = validProducts.map((p) => ({
            ...p,
            floor:
                (p.floor ?? "") !== ""
                    ? p.floor
                    : Number(p.compartimentation ?? p.compartmentation ?? 0) === 1
                        ? normalizeUnitValue(p.unitId ?? p.unit)
                        : p.floor,
            apartment:
                (p.apartment ?? "") !== ""
                    ? p.apartment
                    : Number(p.compartimentation ?? p.compartmentation ?? 0) === 2
                        ? normalizeUnitValue(p.unitId ?? p.unit)
                        : p.apartment,
            is_quantity_edited: p.is_quantity_edited === true,
        }));

        const response = await apiClient.put(`/api/update_offer_items/`, {
            updated_products: payload,
        });

        if (response.status === 200) {
            return response.data;
        } else {
            return alert("Error while updating items");
        }
    } catch (error) {
        console.error("Error while updating items", error);
        showAlertModal(
            context.$store,
            "A apărut o eroare. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
    }
}

/**
 * Creates a new offer and saves the selected products.
 * Sends an API request to create a new offer and attaches necessary data such as client, location, and pricing details.
 * After the offer is created, it saves the selected products.
 * Displays an alert in case of error or failure.
 *
 * @async
 * @function createOffer
 * @param {Object} context - The application context, used for displaying alerts.
 * @param {number} offerType - The type of the offer (e.g. simple or VMC offer).
 * @param {Array} products - The list of products for the offer.
 * @param {Object} offer - The offer details including discount, notes, etc.
 * @param {number} clientId - The ID of the client.
 * @param {number} locationId - The ID of the location associated with the offer.
 * @param {number} agent_id - The ID of the agent creating the offer.
 * @param {number} offerPriceList - The ID of the price list for the offer.
 * @param {Array} [paymentTerms=[]] - Optional array of payment terms.
 * @param {Array} recommendedProducts - Recommended products for the offer.
 * @param {Array} units - Units data for the offer.
 * @returns {Promise<Object|undefined>} The created offer data or undefined in case of error.
 */
export async function createOffer(
    context,
    offerType,
    products,
    offer,
    clientId,
    locationId,
    agent_id,
    offerPriceList,
    paymentTerms = [],
    recommendedProducts,
    units
) {
    try {
        const offerData = {
            agent_id: agent_id,
            client_id: clientId,
            location_id: locationId,
            discount: offer.discount,
            offer_type: offerType,
            notes: offer.notes,
            status: 0,
            invoice: 0,
            cap_money: 0,
            price_list_id: offerPriceList,
            parent_offer: offer.parent_offer,
            payment_terms: paymentTerms,
            units: units,
            list_price_value: offer.totalPriceWithoutDiscount,
            value_with_discount: offer.totalFinalPrice,
            is_discount_mixed: offer.isDiscountMixed
                ? offer.isDiscountMixed
                : false,
            custom_pdf_heading: offer.customPDFHeading,
            location_visible_on_pdf: Boolean(offer.locationVisibleOnPdf),
            area_visible_on_pdf: Boolean(offer.areaVisibleOnPdf),
            selected_customer_type: offer.selectedCustomerType,
        };
        if (offer.compartmentation !== undefined && offer.compartmentation !== null) {
            offerData.compartmentation = offer.compartmentation;
        }
        if (offer.customization !== undefined && offer.customization !== null) {
            offerData.customization = offer.customization;
        }
        if (offer.rooms_grouping !== undefined && offer.rooms_grouping !== null) {
            offerData.rooms_grouping = offer.rooms_grouping;
        }

        if (offer.selectedCustomerType === 1) {
            
            const totalPriceWithoutTax = offer.totalPriceWithoutDiscount / 1.21;
            offerData.list_price_value = totalPriceWithoutTax * 1.21;
        } else {
            offerData.list_price_value = offer.totalPriceWithoutDiscount; // No change if customer type is not 1
        }

        if (recommendedProducts) {
            offerData.recommended_products = recommendedProducts;
        }
        let response;
        if (offerType === 3) {
            response = await apiClient.post(
                "/api/create-vmc-offer/",
                JSON.stringify(offerData),
                {
                    headers: { "Content-Type": "application/json" },
                }
            );
        } else {
            response = await apiClient.post(
                "/api/create-simple-offer/",
                JSON.stringify(offerData),
                {
                    headers: { "Content-Type": "application/json" },
                }
            );
        }
        // Check if response status is 201 else return and throw error
        if (response.status !== 201) {
            alert("Eroare la crearea ofertei: " + response.data.message);
            console.error("Error creating offer:", response);
            return;
        }
        const offerId = response.data.offer.id;

        const updatedProducts = products.map(product => {
            return {
                ...product,
                offer_id: offerId,
                offer: offerId,
                quantity: Number(product.quantity) || 0
            };
        });

        await saveSelectedProducts(context, updatedProducts);
        return response.data.offer;
    } catch (error) {
        console.error("Error creating offer:", error);
        showAlertModal(
            context.$store,
            "A apărut o eroare la crearea ofertei. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
    }
}

/**
 * Updates an existing offer and its associated products.
 * Sends an API request to update the offer and its details such as pricing, units, and payment terms.
 * After the offer is updated, it updates the selected products.
 * Displays an alert in case of error or failure.
 *
 * @async
 * @function updateOffer
 * @param {Object} context - The application context, used for displaying alerts.
 * @param {number} offerType - The type of the offer (e.g. simple or VMC offer).
 * @param {number} offerId - The ID of the offer to be updated.
 * @param {Array} products - The list of products for the offer.
 * @param {Object} offer - The offer details including discount, notes, etc.
 * @param {number} priceListId - The ID of the price list for the offer.
 * @param {Array} paymentTerms - Array of payment terms for the offer.
 * @param {Array} recommendedProducts - Recommended products for the offer.
 * @param {Array} units - Units data for the offer.
 * @returns {Promise<Object|undefined>} The updated products data or undefined in case of error.
 */
export async function updateOffer(
    context,
    offerType,
    offerId,
    products,
    offer,
    priceListId,
    paymentTerms,
    recommendedProducts,
    units
) {
    try {
        const offerData = {
            discount: offer.discount,
            notes: offer.notes,
            status: 0,
            invoice: 0,
            cap_money: 0,
            price_list_id: priceListId,
            payment_terms: paymentTerms,
            units: units,
            list_price_value: offer.totalPriceWithoutDiscount,
            value_with_discount: offer.totalFinalPrice,
            is_discount_mixed: offer.isDiscountMixed
                ? offer.isDiscountMixed
                : false,
            custom_pdf_heading: offer.customPDFHeading,
            location_visible_on_pdf: Boolean(offer.locationVisibleOnPdf),
            area_visible_on_pdf: Boolean(offer.areaVisibleOnPdf),
            selected_customer_type: offer.selectedCustomerType,
        };
        if (offer.compartmentation !== undefined && offer.compartmentation !== null) {
            offerData.compartmentation = offer.compartmentation;
        }
        if (offer.customization !== undefined && offer.customization !== null) {
            offerData.customization = offer.customization;
        }
        if (offer.rooms_grouping !== undefined && offer.rooms_grouping !== null) {
            offerData.rooms_grouping = offer.rooms_grouping;
        }

        if (offer.selectedCustomerType === 1) {
            
            const totalPriceWithoutTax = offer.totalPriceWithoutDiscount / 1.21;
            offerData.list_price_value = totalPriceWithoutTax * 1.21;
        } else {
            offerData.list_price_value = offer.totalPriceWithoutDiscount; // No change if customer type is not 1
        }

        if (recommendedProducts) {
            offerData.recommended_products = recommendedProducts;
        }

        if (offerType === 3) {
            await apiClient.post(
                `/api/update-vmc-offer/${offerId}/`,
                JSON.stringify(offerData),
                {
                    headers: { "Content-Type": "application/json" },
                }
            );
        } else {
            await apiClient.post(
                `/api/update-simple-offer/${offerId}/`,
                JSON.stringify(offerData),
                {
                    headers: { "Content-Type": "application/json" },
                }
            );
        }

        const updatedProducts = products.map(product => {
            return {
                ...product,
                offer_id: offerId,
                offer: offerId,
                quantity: Number(product.quantity) || 0
            };
        });

        return await updateSelectedProducts(context, updatedProducts);
    } catch (error) {
        console.error("Error updating offer:", error);
        showAlertModal(
            context.$store,
            "A apărut o eroare la salvarea ofertei. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
    }
}

/**
 * Changes the general discount for a simple or composite offer.
 * Updates the general discount for a simple or composite offer.
 * Backend recalculates totals and applies the discount to all units/sub-offers.
 *
 * @param {string|number} offerId - Numeric offer id or composite id (e.g. CO12).
 * @param {number} discount - General discount percent (0-100).
 * @returns {Promise<Object>} Updated offer summary from the API.
 */
export async function updateOfferGeneralDiscount(offerId, discount) {
    const response = await apiClient.post("/api/update-offer-general-discount/", {
        offer_id: offerId,
        discount: Number(discount),
    });
    return response.data;
}

// this method checks if the value passed to it is indeed an object, not an Array or null as other type checks might
// return true when checking if an array is an object
export function isObject(value) {
    return typeof value === "object" && !Array.isArray(value) && value !== null;
}

/**
 * Opens a PDF in a new tab.
 * @param {string} url - The URL of the PDF.
 */
export function openPDFInNewTab(url) {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const PDF_FILE_PICKER_TYPES = [
    {
        description: "PDF document",
        accept: { "application/pdf": [".pdf"] },
    },
];

const ZIP_FILE_PICKER_TYPES = [
    {
        description: "ZIP archive",
        accept: {
            "application/zip": [".zip"],
            "application/x-zip-compressed": [".zip"],
        },
    },
];

function ensureFileExtension(filename, extension) {
    const normalizedExt = extension.startsWith(".") ? extension : `.${extension}`;
    const base = String(filename ?? "").trim() || "download";
    if (base.toLowerCase().endsWith(normalizedExt.toLowerCase())) {
        return base;
    }
    return `${base}${normalizedExt}`;
}

function ensurePdfFilename(filename) {
    return ensureFileExtension(filename, ".pdf");
}

function ensureZipFilename(filename) {
    return ensureFileExtension(filename, ".zip");
}

function normalizeBlobMime(blob, mimeType) {
    if (blob instanceof Blob && blob.type === mimeType) {
        return blob;
    }
    return new Blob([blob], { type: mimeType });
}

async function readBlobErrorMessage(blob, fallbackMessage) {
    try {
        const preview = await blob.slice(0, 2048).text();
        const trimmed = preview.trim();
        if (trimmed.startsWith("{")) {
            const parsed = JSON.parse(trimmed);
            return (
                parsed.error ||
                parsed.detail ||
                parsed.message ||
                fallbackMessage
            );
        }
        if (trimmed.startsWith("<")) {
            return fallbackMessage;
        }
    } catch {
        // Fall through to the generic message.
    }
    return fallbackMessage;
}

async function validatePdfBlobResponse(response) {
    const fallbackMessage = `Eroare la descărcarea PDF-ului (${response?.status || "unknown"}).`;
    if (response?.status !== 200) {
        throw new Error(
            await readBlobErrorMessage(response?.data, fallbackMessage)
        );
    }

    const blob = response.data;
    const header = await blob.slice(0, 5).text();
    if (!header.startsWith("%PDF-")) {
        throw new Error(
            await readBlobErrorMessage(
                blob,
                "Fișierul descărcat nu este un PDF valid."
            )
        );
    }

    return normalizeBlobMime(blob, "application/pdf");
}

async function validateZipBlobResponse(response) {
    const fallbackMessage = `Eroare la descărcarea arhivei ZIP (${response?.status || "unknown"}).`;
    if (response?.status !== 200) {
        throw new Error(
            await readBlobErrorMessage(response?.data, fallbackMessage)
        );
    }

    const blob = response.data;
    const header = new Uint8Array(await blob.slice(0, 2).arrayBuffer());
    if (header[0] !== 0x50 || header[1] !== 0x4b) {
        throw new Error(
            await readBlobErrorMessage(
                blob,
                "Fișierul descărcat nu este o arhivă ZIP validă."
            )
        );
    }

    return normalizeBlobMime(blob, "application/zip");
}

async function openPreselectedSaveFilePicker(suggestedName, pickerTypes) {
    return window.showSaveFilePicker({
        suggestedName,
        types: pickerTypes,
    });
}

async function downloadBlobWithUserChoice(blob, filename, pickerTypes) {
    const safeFilename = ensurePdfFilename(filename);
    const canPickPath =
        typeof window !== "undefined" &&
        window.isSecureContext &&
        typeof window.showSaveFilePicker === "function";

    if (canPickPath) {
        try {
            const handle = await openPreselectedSaveFilePicker(
                safeFilename,
                pickerTypes || PDF_FILE_PICKER_TYPES
            );
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            return;
        } catch (error) {
            // Keep user-cancel behavior unchanged.
            if (error?.name === "AbortError") {
                throw error;
            }
            // In async flows, picker may fail due to missing transient user activation.
            // Fall back to regular browser download behavior.
        }
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = safeFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
}

async function downloadZipBlobWithUserChoice(blob, filename) {
    const safeFilename = ensureZipFilename(filename);
    const canPickPath =
        typeof window !== "undefined" &&
        window.isSecureContext &&
        typeof window.showSaveFilePicker === "function";

    if (canPickPath) {
        try {
            const handle = await openPreselectedSaveFilePicker(
                safeFilename,
                ZIP_FILE_PICKER_TYPES
            );
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            return;
        } catch (error) {
            if (error?.name === "AbortError") {
                throw error;
            }
        }
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = safeFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
}

async function writeBlobToFileHandle(fileHandle, blob) {
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
}

function toDateStampFromDate(dateObj) {
    const now = dateObj;
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy}`;
}

function parseDateValue(value) {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
    }
    if (typeof value === "string" || typeof value === "number") {
        const raw = String(value).trim();

        // Backend commonly sends offer_date like "24-04-2026".
        const roStyle = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (roStyle) {
            const dd = Number(roStyle[1]);
            const mm = Number(roStyle[2]);
            const yyyy = Number(roStyle[3]);
            const d = new Date(yyyy, mm - 1, dd);
            if (
                d.getFullYear() === yyyy &&
                d.getMonth() === mm - 1 &&
                d.getDate() === dd
            ) {
                return d;
            }
        }

        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
}

function pickFirstValidDate(...candidates) {
    for (const candidate of candidates) {
        const parsed = parseDateValue(candidate);
        if (parsed) return parsed;
    }
    return null;
}

function getDateStampFromSources(...candidates) {
    const picked = pickFirstValidDate(...candidates) || new Date();
    return toDateStampFromDate(picked);
}

function sanitizeFilenamePart(value, fallback = "X") {
    const clean = String(value ?? "")
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "-")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    return clean || fallback;
}

function normalizeCompositeId(value) {
    const raw = sanitizeFilenamePart(value, "CO");
    return /^co/i.test(raw) ? `CO${raw.slice(2)}` : `CO${raw}`;
}

const OFFER_CATEGORY_FILENAME_BY_TYPE = {
    1: "pardoseala",
    2: "sanitare",
    3: "ventilatie",
    4: "simpla",
    5: "camera-tehnica-si-coloane",
    6: "incalzire-si-racire-prin-tavan",
    7: "incalzire-si-racire-prin-pereti",
    8: "automatizare",
    9: "ventiloconvectoare",
    10: "canal-termic",
    11: "pompe-de-caldura",
    12: "aspirare",
    13: "servicii",
    20: "pardoseala",
};

function getOfferCategoryFilenamePart(_pdfType, context) {
    const headingCandidates = [
        context?.offer?.custom_pdf_heading,
        context?.offer?.customPDFHeading,
        context?.offerInformation?.custom_pdf_heading,
        context?.offerInformation?.customPDFHeading,
        context?.offer?.offer_heading,
        context?.offerInformation?.offer_heading,
        context?.offer?.offerHeading,
        context?.offerInformation?.offerHeading,
    ];

    const heading = headingCandidates.find(
        value => typeof value === "string" && value.trim().length > 0
    );
    if (heading && !["oferta", "ofertă"].includes(heading.trim().toLowerCase())) {
        return sanitizeFilenamePart(heading, "oferta");
    }

    const routeOfferType =
        typeof context?.getOfferType === "function"
            ? context.getOfferType()?.type
            : undefined;
    const offerTypeCandidates = [
        context?.offer?.type,
        context?.offerInformation?.type,
        context?.offer?.offer_type,
        context?.offerInformation?.offer_type,
        context?.offer?.offerType,
        context?.offerInformation?.offerType,
        routeOfferType,
    ];
    const resolvedOfferType = offerTypeCandidates.find(
        value => value !== undefined && value !== null && String(value).trim() !== ""
    );
    const normalizedType = Number(resolvedOfferType);
    if (Number.isFinite(normalizedType) && OFFER_CATEGORY_FILENAME_BY_TYPE[normalizedType]) {
        return sanitizeFilenamePart(
            OFFER_CATEGORY_FILENAME_BY_TYPE[normalizedType],
            "oferta"
        );
    }
    return "oferta";
}

function getFilenameFromContentDisposition(headerValue, fallback) {
    if (!headerValue) return fallback;

    const disposition = String(headerValue);
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1].trim()) || fallback;
        } catch {
            // Fall through to simpler patterns.
        }
    }

    const quotedMatch = disposition.match(/filename="([^"]+)"/i);
    if (quotedMatch?.[1]) return quotedMatch[1].trim();

    const plainMatch = disposition.match(/filename=([^;]+)/i);
    if (plainMatch?.[1]) {
        return plainMatch[1].trim().replace(/^"|"$/g, "") || fallback;
    }

    return fallback;
}

function getDownloadFilenameFromResponse(response, fallback) {
    const headers = response?.headers || {};
    const disposition =
        headers["content-disposition"] || headers["Content-Disposition"];
    return getFilenameFromContentDisposition(disposition, fallback);
}

function getOfferDateCandidates(source) {
    if (!source || typeof source !== "object") return [];
    return [
        source.offer_date,
        source.offerDate,
        source.date_offer,
        source.dateOffer,
        source.modified,
        source.modified_at,
        source.modifiedAt,
        source.date,
        source.created_at,
        source.createdAt,
        source.created_on,
        source.createdOn,
        source.updated_at,
        source.updatedAt,
    ];
}

/**
 * Creates a PDF file for the offer.
 * @param {string} offerId - The ID of the offer.
 * @param {number} type - The type of the PDF (0 - with price, 1 - without price, 2 - general offer, 3 - without price and quantity).
 */
export function createPDFOffer(offerId, type, sorting) {
    const pdfLink = `${process.env.VUE_APP_API_BASE_URL}/api/pdf/offer/${offerId}/${type}/${sorting}/`;
    openPDFInNewTab(pdfLink);
}

// Prevent duplicate offer-PDF job polling/download flows.
const offerPdfInFlight = new Map();

/**
 * Creates an offer PDF via async Celery job and shows errors in a modal.
 * On success downloads the produced PDF.
 *
 * @param {string|number} offerId
 * @param {number|string} type
 * @param {number|string} sorting
 * @param {Object} context Vue component instance (needs context.$store)
 */
function getPdfFlagsSourceOffer(context) {
    const offer = context?.offer;
    if (Array.isArray(offer?.offers) && offer.offers.length) {
        return offer.offers[0];
    }
    return offer;
}

function getSubofferIdsForPdfFlags(context) {
    const offers = context?.offer?.offers;
    if (Array.isArray(offers) && offers.length) {
        return offers
            .map((item) => item?.id)
            .filter((id) => /^\d+$/.test(String(id)));
    }
    const id = String(context?.offerId ?? "");
    return /^\d+$/.test(id) ? [id] : [];
}

function getPdfLocationJobOptions(context) {
    const flagsSource = getPdfFlagsSourceOffer(context);
    const visible = Boolean(
        context?.offerInformation?.locationVisibleOnPdf ??
            flagsSource?.location_visible_on_pdf ??
            false
    );
    const description = String(context?.location?.description ?? "").trim();
    const areaVisible = Boolean(
        context?.offerInformation?.areaVisibleOnPdf ??
            flagsSource?.area_visible_on_pdf ??
            false
    );
    return {
        location_visible_on_pdf: visible,
        location_description: description,
        area_visible_on_pdf: areaVisible,
    };
}

async function persistPdfVisibilityBeforeJob(context) {
    const offerIds = getSubofferIdsForPdfFlags(context);
    if (!offerIds.length) {
        return;
    }
    const {
        location_visible_on_pdf,
        area_visible_on_pdf,
    } = getPdfLocationJobOptions(context);
    const payload = { location_visible_on_pdf, area_visible_on_pdf };
    await Promise.all(
        offerIds.map((id) =>
            apiClient.post(`/api/update-simple-offer/${id}/`, JSON.stringify(payload), {
                headers: { "Content-Type": "application/json" },
            })
        )
    );
}

export async function createPDFOfferJob(offerId, type, sorting, context) {
    const offerIdSafe = String(offerId ?? "");
    const typeSafe = String(type ?? 0);
    const sortingSafe = String(sorting ?? 0);
    const pdfLocation = getPdfLocationJobOptions(context);
    const inFlightKey = `${offerIdSafe}|${typeSafe}|${sortingSafe}|${pdfLocation.location_visible_on_pdf}|${pdfLocation.location_description}|${pdfLocation.area_visible_on_pdf}`;

    if (offerPdfInFlight.has(inFlightKey)) return;

    try {
        await persistPdfVisibilityBeforeJob(context);
    } catch (error) {
        console.warn("Could not persist PDF visibility flags before job:", error);
    }

    const dateStampForSuggestion = getDateStampFromSources(
        ...getOfferDateCandidates(context?.offer),
        ...getOfferDateCandidates(context?.offerInformation)
    );
    const typePart = getOfferCategoryFilenamePart(typeSafe, context);
    const suggestedFilename = `Oferta ${typePart} ${sanitizeFilenamePart(offerIdSafe, "oferta")} din ${dateStampForSuggestion}.pdf`;

    let preselectedSaveHandle = null;
    const canPickPath =
        typeof window !== "undefined" &&
        window.isSecureContext &&
        typeof window.showSaveFilePicker === "function";
    if (canPickPath) {
        try {
            preselectedSaveHandle = await openPreselectedSaveFilePicker(
                ensurePdfFilename(suggestedFilename),
                PDF_FILE_PICKER_TYPES
            );
        } catch (error) {
            if (error?.name === "AbortError") {
                return;
            }
            preselectedSaveHandle = null;
        }
    }

    offerPdfInFlight.set(inFlightKey, true);

    const cleanup = () => {
        if (!offerPdfInFlight.has(inFlightKey)) return;
        offerPdfInFlight.delete(inFlightKey);
        emitter.emit("hide-overlay");
    };

    emitter.emit("show-overlay", "Se pregătește oferta PDF...");

    const payload = {
        offer_id: offerIdSafe,
        type: typeSafe,
        sorting: sortingSafe,
        ...pdfLocation,
    };

    apiClient
        .post("/api/pdf/offer-job/", payload)
        .then(res => {
            const jobId = res?.data?.job_id;
            if (!jobId) throw new Error("Missing job_id in response");

            const statusUrl = `/api/pdf/offer-job/${jobId}/status/`;
            const downloadUrl = `/api/pdf/offer-job/${jobId}/download/`;

            const startedAt = Date.now();
            const MAX_WAIT_MS = 1000 * 60 * 25; // 25 minutes

            let downloaded = false;
            const interval = window.setInterval(async () => {
                try {
                    const st = await apiClient.get(statusUrl);
                    const status = st?.data?.status;

                    if (status === "finished") {
                        if (downloaded) return;
                        downloaded = true;
                        window.clearInterval(interval);
                        const warnings = Array.isArray(st?.data?.warnings)
                            ? st.data.warnings
                            : [];

                        apiClient
                            .get(downloadUrl, { responseType: "blob" })
                            .then(async dRes => {
                                const blob = await validatePdfBlobResponse(dRes);
                                const dateStamp = getDateStampFromSources(
                                    ...getOfferDateCandidates(st?.data),
                                    ...getOfferDateCandidates(context?.offer),
                                    ...getOfferDateCandidates(context?.offerInformation)
                                );
                                const fallbackFilename = `Oferta ${typePart} ${sanitizeFilenamePart(offerIdSafe, "oferta")} din ${dateStamp}.pdf`;
                                const filename = ensurePdfFilename(
                                    getDownloadFilenameFromResponse(
                                        dRes,
                                        fallbackFilename
                                    )
                                );

                                if (preselectedSaveHandle) {
                                    try {
                                        await writeBlobToFileHandle(
                                            preselectedSaveHandle,
                                            blob
                                        );
                                        return;
                                    } catch (error) {
                                        if (error?.name === "AbortError") {
                                            throw error;
                                        }
                                    }
                                }
                                return downloadBlobWithUserChoice(
                                    blob,
                                    filename,
                                    PDF_FILE_PICKER_TYPES
                                );
                            })
                            .then(() => {
                                cleanup();
                                if (warnings.length > 0) {
                                    const warningFiles = warnings.map(w =>
                                        String(w || "").replace(/^Missing media asset:\s*/i, "").trim()
                                    ).filter(Boolean);
                                    const warningIntro =
                                        "Atenție,media lipsă, PDF-ul se va genera fără următoarele poze:";
                                    const warningHtml = `${warningIntro}<br>${warningFiles.join("<br>")}`;
                                    const warningPlain = `${warningIntro}\n${warningFiles.join("\n")}`;
                                    showAlertModal(
                                        context.$store,
                                        warningHtml,
                                        "warning",
                                        12000,
                                        warningPlain
                                    );
                                }
                            })
                            .catch(downloadErr => {
                                cleanup();
                                if (downloadErr?.name === "AbortError") {
                                    return;
                                }
                                showAlertModal(
                                    context.$store,
                                    downloadErr?.message ||
                                        "Eroare la descărcarea PDF-ului ofertei.",
                                    "danger",
                                    12000,
                                    downloadErr
                                );
                            });
                        return;
                    }

                    if (status === "failed") {
                        window.clearInterval(interval);
                        cleanup();
                        const errorMsg =
                            st?.data?.error ||
                            "Eroare la generarea PDF-ului ofertei.";
                        showAlertModal(
                            context.$store,
                            `Eroare la generarea PDF-ului ofertei: ${errorMsg}`,
                            "danger",
                            12000,
                            st?.data?.error || ""
                        );
                        return;
                    }

                    if (Date.now() - startedAt > MAX_WAIT_MS) {
                        window.clearInterval(interval);
                        cleanup();
                        showAlertModal(
                            context.$store,
                            "Timeout la generarea PDF-ului ofertei.",
                            "danger",
                            12000
                        );
                    }
                } catch (err) {
                    window.clearInterval(interval);
                    cleanup();
                    showAlertModal(
                        context.$store,
                        "Eroare la verificarea statusului generării PDF-ului.",
                        "danger",
                        12000,
                        err
                    );
                }
            }, 2000);
        })
        .catch(err => {
            cleanup();
            showAlertModal(
                context.$store,
                "Eroare la pornirea generării PDF-ului ofertei.",
                "danger",
                12000,
                err
            );
        });
}

const compositeOfferPdfInFlight = new Map();

async function createCompositeOfferPdfJob(payload, inFlightKey, context, overlayText) {
    if (compositeOfferPdfInFlight.has(inFlightKey)) return;

    const compositeName = normalizeCompositeId(payload?.composite_id);
    const matchingSuboffer =
        payload?.job_kind === "single" &&
        Array.isArray(context?.offer?.offers)
            ? context.offer.offers.find(
                  sub => String(sub?.id) === String(payload?.offer_id)
              )
            : null;
    const suggestionDateStamp = getDateStampFromSources(
        ...getOfferDateCandidates(matchingSuboffer),
        ...getOfferDateCandidates(context?.offer),
        ...getOfferDateCandidates(context?.offerInformation)
    );
    const categoryPart = getOfferCategoryFilenamePart(payload?.type, context);
    const suggestedFilename =
        payload?.job_kind === "single"
            ? `Oferta ${categoryPart} ${compositeName} ${sanitizeFilenamePart(payload?.unit_label, "unit")} din ${suggestionDateStamp}.pdf`
            : `Oferta ${categoryPart} ${compositeName} din ${suggestionDateStamp}.pdf`;

    let preselectedSaveHandle = null;
    const canPickPath =
        typeof window !== "undefined" &&
        window.isSecureContext &&
        typeof window.showSaveFilePicker === "function";
    if (canPickPath) {
        try {
            preselectedSaveHandle = await openPreselectedSaveFilePicker(
                ensurePdfFilename(suggestedFilename),
                PDF_FILE_PICKER_TYPES
            );
        } catch (error) {
            if (error?.name === "AbortError") {
                return;
            }
            preselectedSaveHandle = null;
        }
    }

    compositeOfferPdfInFlight.set(inFlightKey, true);
    emitter.emit("show-overlay", overlayText);

    let cleanedUp = false;
    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        compositeOfferPdfInFlight.delete(inFlightKey);
        emitter.emit("hide-overlay");
    };

    apiClient
        .post("/api/pdf/composite-offer-job/", payload)
        .then(res => {
            const jobId = res?.data?.job_id;
            if (!jobId) throw new Error("Missing job_id in response");

            const statusUrl = `/api/pdf/composite-offer-job/${jobId}/status/`;
            const downloadUrl = `/api/pdf/composite-offer-job/${jobId}/download/`;
            const startedAt = Date.now();
            const MAX_WAIT_MS = 1000 * 60 * 25;

            let downloaded = false;
            const interval = window.setInterval(async () => {
                try {
                    const st = await apiClient.get(statusUrl);
                    const status = st?.data?.status;

                    if (status === "finished") {
                        if (downloaded) return;
                        downloaded = true;
                        window.clearInterval(interval);
                        const warnings = Array.isArray(st?.data?.warnings)
                            ? st.data.warnings
                            : [];

                        apiClient
                            .get(downloadUrl, { responseType: "blob" })
                            .then(async dRes => {
                                const blob = await validatePdfBlobResponse(dRes);
                                const dateStamp = getDateStampFromSources(
                                    ...getOfferDateCandidates(st?.data),
                                    ...getOfferDateCandidates(matchingSuboffer),
                                    ...getOfferDateCandidates(context?.offer),
                                    ...getOfferDateCandidates(context?.offerInformation)
                                );
                                const fallbackFilename =
                                    payload?.job_kind === "single"
                                        ? `Oferta ${categoryPart} ${compositeName}-${sanitizeFilenamePart(payload?.unit_label, "unit")} din ${dateStamp}.pdf`
                                        : `Oferta ${categoryPart} ${compositeName} din ${dateStamp}.pdf`;
                                const filename = ensurePdfFilename(
                                    getDownloadFilenameFromResponse(
                                        dRes,
                                        fallbackFilename
                                    )
                                );

                                if (preselectedSaveHandle) {
                                    try {
                                        await writeBlobToFileHandle(
                                            preselectedSaveHandle,
                                            blob
                                        );
                                        return;
                                    } catch (error) {
                                        if (error?.name === "AbortError") {
                                            throw error;
                                        }
                                    }
                                }
                                return downloadBlobWithUserChoice(
                                    blob,
                                    filename,
                                    PDF_FILE_PICKER_TYPES
                                );
                            })
                            .then(() => {
                                cleanup();

                                if (warnings.length > 0 && context?.$store) {
                                    const warningFiles = warnings
                                        .map(w =>
                                            String(w || "")
                                                .replace(/^Missing media asset:\s*/i, "")
                                                .trim()
                                        )
                                        .filter(Boolean);
                                    const warningIntro =
                                        "Atenție,media lipsă, PDF-ul se va genera fără următoarele poze:";
                                    const warningHtml = `${warningIntro}<br>${warningFiles.join("<br>")}`;
                                    const warningPlain = `${warningIntro}\n${warningFiles.join("\n")}`;
                                    showAlertModal(
                                        context.$store,
                                        warningHtml,
                                        "warning",
                                        12000,
                                        warningPlain
                                    );
                                }
                            })
                            .catch(downloadErr => {
                                cleanup();
                                if (downloadErr?.name === "AbortError") {
                                    return;
                                }
                                if (context?.$store) {
                                    showAlertModal(
                                        context.$store,
                                        downloadErr?.message ||
                                            "Eroare la descărcarea PDF-ului compus.",
                                        "danger",
                                        12000,
                                        downloadErr
                                    );
                                }
                            });
                        return;
                    }

                    if (status === "failed") {
                        window.clearInterval(interval);
                        cleanup();
                        const errorMsg =
                            st?.data?.error || "Eroare la generarea PDF-ului compus.";
                        if (context?.$store) {
                            showAlertModal(
                                context.$store,
                                `Eroare la generarea PDF-ului compus: ${errorMsg}`,
                                "danger",
                                12000,
                                st?.data?.error || ""
                            );
                        }
                        return;
                    }

                    if (Date.now() - startedAt > MAX_WAIT_MS) {
                        window.clearInterval(interval);
                        cleanup();
                        if (context?.$store) {
                            showAlertModal(
                                context.$store,
                                "Timeout la generarea PDF-ului compus.",
                                "danger",
                                12000
                            );
                        }
                    }
                } catch (err) {
                    window.clearInterval(interval);
                    cleanup();
                    if (context?.$store) {
                        showAlertModal(
                            context.$store,
                            "Eroare la verificarea statusului generării PDF-ului compus.",
                            "danger",
                            12000,
                            err
                        );
                    }
                }
            }, 2000);
        })
        .catch(err => {
            cleanup();
            if (context?.$store) {
                showAlertModal(
                    context.$store,
                    "Eroare la pornirea generării PDF-ului compus.",
                    "danger",
                    12000,
                    err
                );
            }
        });
}

export async function createPDFCompositeOffer(
    offerId,
    type,
    sorting,
    unitLabel,
    compositeId,
    index,
    total,
    context = null
) {
    const pdfFlags = getPdfLocationJobOptions(context);
    try {
        await persistPdfVisibilityBeforeJob(context);
    } catch (error) {
        console.warn("Could not persist PDF visibility flags before composite job:", error);
    }
    const inFlightKey = `single|${offerId}|${type}|${sorting}|${compositeId}|${unitLabel}|${index}|${total}|${pdfFlags.location_visible_on_pdf}|${pdfFlags.area_visible_on_pdf}`;
    createCompositeOfferPdfJob(
        {
            job_kind: "single",
            offer_id: offerId,
            type,
            sorting,
            unit_label: unitLabel,
            composite_id: compositeId,
            index,
            total,
            ...pdfFlags,
        },
        inFlightKey,
        context,
        "Se pregătește PDF-ul subofertei..."
    );
}

export async function createPDFCompositeTotalOffer(
    compositeId,
    type = 0,
    sorting = 0,
    context = null
) {
    const pdfFlags = getPdfLocationJobOptions(context);
    try {
        await persistPdfVisibilityBeforeJob(context);
    } catch (error) {
        console.warn("Could not persist PDF visibility flags before composite total job:", error);
    }
    const inFlightKey = `total|${compositeId}|${type}|${sorting}|${pdfFlags.location_visible_on_pdf}|${pdfFlags.area_visible_on_pdf}`;
    createCompositeOfferPdfJob(
        {
            job_kind: "total",
            composite_id: compositeId,
            type,
            sorting,
            ...pdfFlags,
        },
        inFlightKey,
        context,
        "Se pregătește PDF-ul ofertei compuse..."
    );
}

// Prevent duplicate ZIP polling/download flows in case the UI triggers
// `createPDFCompositeOffersZip()` multiple times for the same parameters.
const compositeOffersZipInFlight = new Map();

/**
 * Creates ZIP with multiple composite sub-offer PDFs.
 * @param {string|number} compositeId Frontend composite id (usually "CO<number>")
 * @param {Array<string|number>} offerIds Sub-offer offer ids
 * @param {number|string} type PDF type (0,1,2,3)
 * @param {number} sorting Sorting parameter (usually 0)
 */
export async function createPDFCompositeOffersZip(
    compositeId,
    offerIds,
    type,
    sorting = 0,
    context = null
) {
    const categoryPart = getOfferCategoryFilenamePart(type, context);
    const dateStamp = getDateStampFromSources(
        ...getOfferDateCandidates(context?.offer),
        ...getOfferDateCandidates(context?.offerInformation)
    );
    const defaultFilename = `Oferta ${categoryPart} ${normalizeCompositeId(compositeId)} din ${dateStamp}.zip`;

    const normalizedOfferIds = Array.isArray(offerIds)
        ? offerIds.map(x => Number(x))
        : [];
    const offerIdsSorted = [...normalizedOfferIds].sort((a, b) => a - b);
    const inFlightKey = `${compositeId}|${offerIdsSorted.join(",")}|${type}|${sorting}`;
    if (compositeOffersZipInFlight.has(inFlightKey)) return;

    let preselectedSaveHandle = null;
    const canPickPath =
        typeof window !== "undefined" &&
        window.isSecureContext &&
        typeof window.showSaveFilePicker === "function";
    if (canPickPath) {
        try {
            preselectedSaveHandle = await openPreselectedSaveFilePicker(
                ensureZipFilename(defaultFilename),
                ZIP_FILE_PICKER_TYPES
            );
        } catch (error) {
            if (error?.name === "AbortError") {
                return;
            }
            preselectedSaveHandle = null;
        }
    }

    compositeOffersZipInFlight.set(inFlightKey, true);
    emitter.emit("show-overlay", "Se pregătește arhiva ZIP...");

    let cleanedUp = false;
    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        compositeOffersZipInFlight.delete(inFlightKey);
        emitter.emit("hide-overlay");
    };

    const payload = {
        composite_id: compositeId,
        offer_ids: offerIdsSorted,
        type,
        sorting,
    };

    apiClient
        .post("/api/pdf/composite-offers-zip-job/", payload)
        .then(res => {
            const jobId = res?.data?.job_id;
            if (!jobId) {
                throw new Error("Missing job_id in response");
            }

            const statusUrl = `/api/pdf/composite-offers-zip-job/${jobId}/status/`;
            const downloadUrl = `/api/pdf/composite-offers-zip-job/${jobId}/download/`;

            const startedAt = Date.now();
            const MAX_WAIT_MS = 1000 * 60 * 25; // 25 minutes

            let downloaded = false;
            const interval = window.setInterval(async () => {
                try {
                    const st = await apiClient.get(statusUrl);
                    const status = st?.data?.status;

                    if (status === "finished") {
                        if (downloaded) return;
                        downloaded = true;
                        window.clearInterval(interval);
                        const warnings = Array.isArray(st?.data?.warnings)
                            ? st.data.warnings
                            : [];
                        // Download with axios so Authorization header is included.
                        apiClient
                            .get(downloadUrl, { responseType: "blob" })
                            .then(async res => {
                                const blob = await validateZipBlobResponse(res);
                                const filename = ensureZipFilename(
                                    getDownloadFilenameFromResponse(
                                        res,
                                        defaultFilename
                                    )
                                );
                                if (preselectedSaveHandle) {
                                    try {
                                        await writeBlobToFileHandle(
                                            preselectedSaveHandle,
                                            blob
                                        );
                                        return;
                                    } catch (error) {
                                        if (error?.name === "AbortError") {
                                            throw error;
                                        }
                                    }
                                }
                                return downloadZipBlobWithUserChoice(blob, filename);
                            })
                            .then(() => {
                                cleanup();
                                if (warnings.length > 0) {
                                    const warningFiles = warnings.map(w =>
                                        String(w || "")
                                            .replace(/^Missing media asset:\s*/i, "")
                                            .trim()
                                    ).filter(Boolean);
                                    const warningIntro =
                                        "Atenție, PDF-urile se vor genera fără următoarele poze:";
                                    const warningHtml = `${warningIntro}<br>${warningFiles.join("<br>")}`;
                                    const warningPlain = `${warningIntro}\n${warningFiles.join("\n")}`;
                                    if (context?.$store) {
                                        showAlertModal(
                                            context.$store,
                                            warningHtml,
                                            "warning",
                                            12000,
                                            warningPlain
                                        );
                                    } else {
                                        // eslint-disable-next-line no-alert
                                        alert(warningPlain);
                                    }
                                }
                            })
                            .catch(downloadErr => {
                                console.error(downloadErr);
                                if (downloadErr?.name === "AbortError") {
                                    cleanup();
                                    return;
                                }
                                // eslint-disable-next-line no-alert
                                alert(
                                    downloadErr?.message ||
                                        "Eroare la descarcarea ZIP-ului."
                                );
                                cleanup();
                            });
                        return;
                    }

                    if (status === "failed") {
                        window.clearInterval(interval);
                        cleanup();
                        // eslint-disable-next-line no-alert
                        alert(
                            `Eroare la generarea ZIP-ului: ${st?.data?.error || ""}`
                        );
                        return;
                    }

                    if (Date.now() - startedAt > MAX_WAIT_MS) {
                        window.clearInterval(interval);
                        cleanup();
                        // eslint-disable-next-line no-alert
                        alert("Timeout la generarea ZIP-ului.");
                    }
                } catch (err) {
                    window.clearInterval(interval);
                    cleanup();
                    // eslint-disable-next-line no-console
                    console.error(err);
                    // eslint-disable-next-line no-alert
                    alert("Eroare la verificarea statusului ZIP-ului.");
                }
            }, 7000);
        })
        .catch(err => {
            // eslint-disable-next-line no-console
            console.error(err);
            // eslint-disable-next-line no-alert
            alert("Eroare la pornirea generării ZIP-ului.");
            cleanup();
        });
}

/**
 * Creates a PDF file for the contract and opens it in a new tab.
 * @param {string} clientId - The ID of the client.
 * @param {int} documentType - The type of the document that needs to be generated.
 * @param contractValue
 */
export function createPDFContract(clientId, documentType, contractValue) {
    const pdfLink = `${process.env.VUE_APP_API_BASE_URL}/api/pdf/generate_pdf_contract/?client_id=${clientId}&document_type=${documentType}&contract_value=${contractValue}`;
    openPDFInNewTab(pdfLink);
}

/**
 * Creates a PDF file for the summary and opens it in a new tab.
 * @param {string} summaryId - The ID of the client.
 */
export function createPDFSummary(summaryId) {
    const pdfLink = `${process.env.VUE_APP_API_BASE_URL}/api/pdf/summary/${summaryId}/`;
    openPDFInNewTab(pdfLink);
}

/**
 * Creates a PDF file for the dimensioning of the location.
 * @param {string} offerId - The ID of the offer.
 * @param {string} locationId - The ID of the location.
 * @param {number} sorting - The sorting type (0 - no sorting, 1 - by air type, 2 - by floor).
 */
export function createPDFDimensioning(offerId, locationId, sorting) {
    const pdfLink = `${process.env.VUE_APP_API_BASE_URL}/api/pdf/dimensioning/${offerId}/${locationId}/${sorting}`;
    openPDFInNewTab(pdfLink);
}

/** ISO YYYY-MM-DD (sau prefix) → afișare DD.MM.YYYY pentru liste preț. */
function formatPriceListDateRo(raw) {
    if (raw == null || raw === "") {
        return "";
    }
    const s = String(raw).slice(0, 10);
    const parts = s.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
        const [y, m, d] = parts;
        return `${d}.${m}.${y}`;
    }
    return s;
}

/**
 * v-select title for a price list: dată aplicare în format Zi.Lună.An; cu valid_from adaugă sufixul.
 * @param {{ date_applied?: string|null, valid_from?: string|null }} priceList
 * @returns {string}
 */
export function priceListSelectTitle(priceList) {
    if (!priceList) {
        return "";
    }
    const da = formatPriceListDateRo(priceList.date_applied);
    const vf = priceList.valid_from;
    if (vf == null || vf === "") {
        return da;
    }
    const s = String(vf).slice(0, 10);
    const parts = s.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
        const [y, m, d] = parts;
        return `${da} - Valabilă din ${d}.${m}.${y}`;
    }
    return da;
}

/**
 * Whether a price list application date is today or in the past (not scheduled for the future).
 * @param {string|null|undefined} dateApplied - ISO date string (YYYY-MM-DD).
 * @returns {boolean}
 */
export function isPriceListDateActive(dateApplied) {
    if (!dateApplied) {
        return false;
    }
    const today = new Date();
    const itemDate = new Date(`${String(dateApplied).slice(0, 10)}T00:00:00`);
    return itemDate <= today;
}

/**
 * Latest price list whose application date is today or in the past.
 * @param {Array<{ id: number, date_applied?: string }>} priceLists
 * @returns {Object|null}
 */
export function getLatestActivePriceList(priceLists) {
    if (!Array.isArray(priceLists)) {
        return null;
    }
    return (
        priceLists
            .filter((priceList) => isPriceListDateActive(priceList.date_applied))
            .sort(
                (a, b) =>
                    new Date(`${b.date_applied}T00:00:00`) -
                    new Date(`${a.date_applied}T00:00:00`)
            )[0] || null
    );
}

/**
 * Whether the current user can see products hidden from the offer program.
 * @returns {boolean}
 */
export function canViewHiddenOfferProducts() {
    const superUser = JSON.parse(sessionStorage.getItem("superUser") || "false");
    const technicalUser = JSON.parse(sessionStorage.getItem("technicalUser") || "false");
    return Boolean(superUser || technicalUser);
}

/**
 * Cancelled products must never appear in the offer program product picker.
 * @param {Array|null|undefined} items
 * @returns {Array}
 */
export function excludeAnulatOfferProducts(items) {
    if (!Array.isArray(items)) {
        return [];
    }
    return items.filter(
        (item) => String(item?.status || "").trim() !== "anulat",
    );
}

function priceListProductsCacheMatchesUserScope(cachedProducts) {
    if (!cachedProducts) {
        return false;
    }

    const cachedIncludesHidden = Boolean(cachedProducts.includesHiddenOfferProducts);
    return cachedIncludesHidden === canViewHiddenOfferProducts();
}

/** In-memory fallback when sessionStorage quota cannot hold the price list. */
let memoryPriceListProductsCache = null;

/**
 * Offer cache must stay under sessionStorage quota (~5MB). pricing_detail /
 * furnizor graphs from the 2026 list blow past that; ofertare only needs
 * code/name/price/categories for the product table.
 * @param {object} item
 * @returns {object}
 */
function slimOfferCacheItem(item) {
    if (!item || typeof item !== "object") {
        return item;
    }
    const rest = { ...item };
    delete rest.pricing_detail;
    delete rest.has_pricing_detail;
    return rest;
}

function buildOfferProductsCachePayload(responseData, items) {
    return {
        price_list_id: responseData.price_list_id,
        modified: responseData.modified,
        type: responseData.type,
        items: (items || []).map(slimOfferCacheItem),
        includesHiddenOfferProducts: canViewHiddenOfferProducts(),
    };
}

function readOfferProductsCache() {
    try {
        const raw = sessionStorage.getItem("PRICE_LIST_PRODUCTS");
        if (raw) {
            return JSON.parse(raw);
        }
    } catch (error) {
        console.warn("Failed to read PRICE_LIST_PRODUCTS cache:", error);
    }
    return memoryPriceListProductsCache;
}

function persistOfferProductsCache(payload) {
    memoryPriceListProductsCache = payload;
    const json = JSON.stringify(payload);
    try {
        sessionStorage.setItem("PRICE_LIST_PRODUCTS", json);
        return true;
    } catch (error) {
        const isQuota =
            error?.name === "QuotaExceededError" ||
            error?.code === 22 ||
            error?.code === 1014;
        if (!isQuota) {
            throw error;
        }
        try {
            sessionStorage.removeItem("PRICE_LIST_PRODUCTS");
            sessionStorage.setItem("PRICE_LIST_PRODUCTS", json);
            return true;
        } catch (retryError) {
            console.warn(
                "PRICE_LIST_PRODUCTS kept in memory only (sessionStorage quota).",
                retryError
            );
            return false;
        }
    }
}

/**
 * Clears cached offer products when the uploaded/updated list type matches.
 * @param {string|null} priceListType
 */
export function invalidatePriceListProductsCache(priceListType = null) {
    const cached = readOfferProductsCache();
    if (!cached) {
        return;
    }
    if (priceListType == null || cached.type === priceListType) {
        sessionStorage.removeItem("PRICE_LIST_PRODUCTS");
        memoryPriceListProductsCache = null;
    }
}

async function fetchAndCachePriceListProducts(context, priceListType) {
    const response = await apiClient.get("/api/price_lists/items/", {
        params: {
            type: priceListType,
        },
    });
    if (!response || !response.data) {
        throw new Error("Failed to fetch product data");
    }

    const items = excludeAnulatOfferProducts(response.data.items);
    persistOfferProductsCache(
        buildOfferProductsCachePayload(response.data, items)
    );

    return {
        items,
        price_list_id: response.data.price_list_id,
        modified: response.data.modified,
        type: response.data.type,
    };
}

/**
 * Fetches price lists from the server and stores them in sessionStorage.
 * The function makes an asynchronous API call to retrieve the price lists.
 * If successful, it stores the data in sessionStorage under the key 'priceLists'.
 * @returns {Promise<Object[]|undefined>} A promise that resolves to an array of price list objects, or undefined if an error occurs.
 */
export async function fetchPriceLists(context, priceListType) {
    try {
        const response = await apiClient.get("/api/price_lists", {
            params: {
                type: priceListType,
            },
        });

        if (response.status === 200) {
            const priceLists = response.data;
            sessionStorage.setItem("priceLists", JSON.stringify(priceLists));
            return priceLists;
        } else {
            throw new Error(`Unexpected response status: ${response.status}`);
        }
    } catch (error) {
        showAlertModal(
            context.$store,
            "A apărut o eroare la fetch-ul listelor de preț. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
        console.error("Error fetching price lists:", error);
    }
}

/**
 * Fetches product data from the server.
 * This function makes an asynchronous API call to retrieve products and their associated price list ID.
 * It assumes the response will contain an object with 'items' and 'price_list_id' properties.
 *
 * @returns {Promise<{items: Object[], priceListId: string}>} A promise that resolves to an object containing an array of items and a price list ID.
 * @throws Will throw an error if the API call is unsuccessful.
 */
export async function fetchProducts(context, priceListType) {
    let priceListProducts = readOfferProductsCache();
    if (
        !priceListProducts ||
        priceListType !== priceListProducts.type ||
        !priceListProductsCacheMatchesUserScope(priceListProducts)
    ) {
        try {
            return await fetchAndCachePriceListProducts(context, priceListType);
        } catch (error) {
            console.error("Error fetching product data:", error);
            showAlertModal(
                context.$store,
                "A apărut o eroare la fetch-ul produselor. Vă rugăm să încercați din nou.",
                "danger",
                12000,
                error
            );
            throw error;
        }
    }

    const priceListId = priceListProducts.price_list_id;
    try {
        const [modifiedResponse, priceLists] = await Promise.all([
            apiClient.get(`/api/price_lists/${priceListId}/modified/`),
            fetchPriceLists(context, priceListType),
        ]);
        if (!modifiedResponse || !modifiedResponse.data) {
            throw new Error("Failed to fetch modified date");
        }

        const backendModifiedDate = modifiedResponse.data.modified;
        const sessionModifiedDate = priceListProducts.modified;
        const latestActivePriceList = getLatestActivePriceList(priceLists);
        const latestActivePriceListId = latestActivePriceList?.id;
        const modifiedChanged = backendModifiedDate !== sessionModifiedDate;
        const newerActivePriceListAvailable =
            latestActivePriceListId != null &&
            Number(latestActivePriceListId) !== Number(priceListId);

        if (modifiedChanged || newerActivePriceListAvailable) {
            return await fetchAndCachePriceListProducts(context, priceListType);
        }

        return {
            ...priceListProducts,
            items: excludeAnulatOfferProducts(priceListProducts.items),
        };
    } catch (error) {
        // Stale cache after list delete/replace (e.g. reimport) → 404 on /modified/.
        // Drop cache and refetch instead of blocking client/offer entry.
        const status = error?.response?.status;
        if (status === 404) {
            invalidatePriceListProductsCache(priceListType);
            try {
                return await fetchAndCachePriceListProducts(context, priceListType);
            } catch (refetchError) {
                console.error(
                    "Error refetching product data after stale price list cache:",
                    refetchError
                );
                showAlertModal(
                    context.$store,
                    "A apărut o eroare la fetch-ul produselor. Vă rugăm să încercați din nou.",
                    "danger",
                    12000,
                    refetchError
                );
                throw refetchError;
            }
        }
        console.error(
            "Error checking modified date or fetching product data:",
            error
        );
        showAlertModal(
            context.$store,
            "A apărut o eroare la fetch-ul produselor. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
        throw error;
    }
}

/**
 * Fetches the products associated with a specific price list by its ID.
 * Sends an API request to retrieve the products for the provided price list.
 *
 * @async
 * @function fetchPriceListProducts
 * @param {number|string} priceListId - The ID of the price list to fetch products for.
 * @param {object} context - The Vue.js context or component instance, used to access the store for error handling.
 * @returns {Promise<Array|undefined>} A promise that resolves to the list of products for the price list, or undefined if an error occurs.
 * @throws {Error} Will throw an error if the API request fails.
 */
export async function fetchPriceListProducts(priceListId, context) {
    try {
        const response = await apiClient.get(
            `/api/price_lists/${priceListId}/items/`
        );
        return {
            ...response.data,
            items: excludeAnulatOfferProducts(response.data?.items),
        };
    } catch (error) {
        console.error("Error fetching price list products:", error);
        showAlertModal(
            context.$store,
            "A apărut o eroare la fetch-ul produselor pe baza unei liste de preț. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
        // Optionally, you can throw the error to be handled by the calling function
        throw error;
    }
}

/**
 * Retrieves client information based on the provided client ID.
 *
 * This function makes an asynchronous HTTP GET request to fetch client data. It expects
 * a client ID and returns the client's information if found. In case the client is not
 * found (404 error), it redirects to the "Page Not Found" route. For other errors, it
 * logs the error message to the console and throws an exception.
 *
 * @param {number|string} clientId - The unique identifier of the client.
 * @param context
 * @returns {Promise<Object|null>} A promise that resolves to the client's information object or null if not found.
 * @throws {Error} Throws an error for any network or server issues, except for 404 Not Found.
 */
export async function getClientInfo(clientId, context) {
    try {
        const clientResponse = await apiClient.get(
            `/api/get_client/${clientId}/`
        );
        return clientResponse.data.client;
    } catch (e) {
        if (e.response && e.response.status === 404) {
            // Push to the error route with a state indicating a missing client
            router.push({
                path: "/:pathMatch(.*)*",
                query: { error: "client-not-found", clientId: clientId },
            });
            return null;
        } else {
            console.error("Error fetching client data:", e);
            showAlertModal(
                context.$store,
                "A apărut o eroare la fetch-ul detaliilor clientului. Vă rugăm să încercați din nou.",
                "danger",
                12000,
                e
            );
            throw e; // Re-throw the error for caller to handle
        }
    }
}

/**
 * Fetches location details by location ID.
 *
 * This function first attempts to retrieve the location details from sessionStorage.
 * If not found in sessionStorage, it makes an asynchronous HTTP GET request to fetch
 * the location details from the server. If the location is found, it returns the location data.
 * In case the location is not found (404 error), it redirects to a "Page Not Found" route.
 * For other types of errors, it logs the error message and re-throws the error.
 *
 * @param {number|string} locationId - The unique identifier of the location.
 * @param context
 * @returns {Promise<Object|null>} A promise that resolves to the location's data object or null if the location is not found.
 * @throws {Error} Throws an error for any network or server issues, except for 404 Not Found.
 */
export async function fetchLocationById(locationId, context) {
    let sessionLocation = await JSON.parse(
        sessionStorage.getItem("location" + locationId)
    );
    if (sessionLocation) {
        return sessionLocation;
    } else {
        try {
            const locationResponse = await apiClient.get(
                `/api/location/${locationId}/`
            );
            return locationResponse.data;
        } catch (e) {
            if (e.response && e.response.status === 404) {
                router.push({
                    path: "/:pathMatch(.*)*",
                    query: {
                        error: "location-not-found",
                        locationId: locationId,
                    },
                });
                return null;
            } else {
                showAlertModal(
                    context.$store,
                    "A apărut o eroare la fetch-ul locatiei. Vă rugăm să încercați din nou.",
                    "danger",
                    12000,
                    e
                );
                console.error("Error fetching location data:", e);
                throw e; // Re-throw the error for caller to handle
            }
        }
    }
}

/**
 * Fetches documents associated with a specific location by its ID.
 * Sends an API request to retrieve the documents for the provided location.
 *
 * @async
 * @function fetchDocuments
 * @param {number|string} locationId - The ID of the location to fetch documents for.
 * @param {object} context - The Vue.js context or component instance, used to access the store for error handling.
 * @returns {Promise<Array|undefined>} A promise that resolves to the list of documents for the location, or undefined if an error occurs.
 */
export async function fetchDocuments(locationId, context) {
    try {
        const docResponse = await apiClient.get(
            "/api/locations/" + locationId + "/documents/"
        );
        return docResponse.data;
    } catch (error) {
        showAlertModal(
            context.$store,
            "A apărut o eroare la fetch-ul documentelor locatiei. Vă rugăm să încercați din nou.",
            "danger",
            12000,
            error
        );
        console.error(error);
    }
}

/**
 * Fetches an offer along with its items based on the provided offer ID.
 *
 * This function makes an asynchronous HTTP GET request to retrieve an offer and its associated items.
 * If the offer is found, it returns the offer data. In case the offer is not found (404 error),
 * it redirects to a "Page Not Found" route. For other types of errors, it logs the error and re-throws it.
 *
 * @param {number|string} offerId - The unique identifier of the offer.
 * @returns {Promise<Object|null>} A promise that resolves to the offer's data object or null if the offer is not found.
 * @throws {Error} Throws an error for any network or server issues, except for 404 Not Found.
 */
export async function fetchOfferWithItemsById(offerId, context) {
    try {
        const offerItemsResponse = await apiClient.get(
            `/api/offer_with_items/${offerId}/`
        );
        return offerItemsResponse.data;
    } catch (e) {
        if (e.response && e.response.status === 404) {
            router.push({
                path: "/:pathMatch(.*)*",
                query: { error: "offer-not-found", offerId: offerId },
            });
            return null;
        } else {
            showAlertModal(
                context.$store,
                "A apărut o eroare la fetch-ul ofertei și a produselor acesteia. Vă rugăm să încercați din nou.",
                "danger",
                12000,
                e
            );
            console.error("Error fetching offer data:", e);
            throw e;
        }
    }
}

/**
 * Triggers an alert modal in the application.
 * This function commits a series of mutations to the Vuex store to configure and show an alert modal.
 * @param {Object} context - The Vuex context object, which provides access to store properties and commit mutations.
 * @param {string} message - The message to be displayed in the alert modal.
 * @param {string} type - The type of the alert (e.g., 'success', 'error', 'warning').
 * @param {number} duration - The duration in milliseconds for which the alert should be displayed.
 * @param errorDetails
 */
export function showAlertModal(
    context,
    message,
    type,
    duration,
    errorDetails = ""
) {
    if (type === "danger") {
        // Clear any existing alert and queue
        context.commit("clearCurrentAlert");
        context.dispatch("configureAlert", {
            message,
            type,
            duration,
            visible: true,
            errorDetails,
        });
    } else {
        const currentAlertType = context.state.alert.type;
        if (currentAlertType === "danger" && context.state.alert.show) {
            // Queue the alert if a danger alert is currently being displayed
            context.commit("queueAlert", {
                message,
                type,
                duration,
                errorDetails,
            });
        } else {
            // Show the alert immediately
            context.dispatch("configureAlert", {
                message,
                type,
                duration,
                visible: true,
                errorDetails,
            });
        }
    }
}

/**
 * Formats a number according to the locale and currency preferences.
 *
 * @param {Object} context - The Vue component context to access global state.
 * @param {number} number - The number to be formatted.
 * @param {boolean} [currency=true] - Whether to display currency symbol.
 * @param {number} [decimals=2] - The number of decimal places to display.
 * @returns {string} The formatted number string with or without currency symbol.
 */
export function formatNumber(context, number, currency = true, decimals = 2) {
    let currencyShort = currency
        ? context.$store.state.currency[context.$store.state.locale].code
        : "";
    return (
        new Intl.NumberFormat(context.$store.state.locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(number) + currencyShort
    );
}

/**
 * Display label for a currency amount suffix (RON → lei, others → EUR/PLN/…).
 * @param {string|null|undefined} currency
 * @returns {string} Empty string when currency is missing.
 */
export function formatPriceCurrencySuffix(currency) {
    const normalized = String(currency || '').trim().toLowerCase();
    if (!normalized) {
        return '';
    }
    if (normalized === 'ron') {
        return 'lei';
    }
    if (normalized === 'euro' || normalized === 'eur') {
        return 'EUR';
    }
    if (normalized === 'pln') {
        return 'PLN';
    }
    if (normalized === 'usd') {
        return 'USD';
    }
    return normalized.toUpperCase();
}

/**
 * Formats a price for display. Returns em dash for empty values.
 * @param {number|string|null|undefined} value
 * @param {number} [decimals=2] - Fraction digits (supplier list prices use 4, like the builder).
 * @param {boolean|string} [currencyOrWithLei=false] - `true` appends " lei"; a currency
 *   code appends that currency's display label (ron → lei, euro → EUR, …).
 * @returns {string}
 */
export function formatRonPrice(value, decimals = 2, currencyOrWithLei = false) {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const num = Number(value);
    if (!Number.isFinite(num)) {
        return '—';
    }
    const maxDigits = Number.isFinite(decimals) ? decimals : 2;
    // Match builder numeric display: don't pad trailing zeros beyond money (2 dp).
    const minDigits = maxDigits > 2 ? 0 : maxDigits;
    const formatted = num.toLocaleString('ro-RO', {
        minimumFractionDigits: minDigits,
        maximumFractionDigits: maxDigits,
    });
    let suffix = '';
    if (currencyOrWithLei === true) {
        suffix = 'lei';
    } else if (typeof currencyOrWithLei === 'string') {
        suffix = formatPriceCurrencySuffix(currencyOrWithLei);
    }
    return suffix ? `${formatted} ${suffix}` : formatted;
}

/**
 * Calculates the RGB color for a given value based on how far it is from the minimum or maximum limit.
 * The color transitions smoothly between red, green, and blue based on the value's percentage in the range.
 *
 * @function getTextScaleColor
 * @param {number} value - The current value to calculate the color for.
 * @param {number} maxLimit - The maximum limit of the value range.
 * @param {boolean} [ascending=true] - If true, colors will transition from lower to higher values; otherwise, the opposite.
 * @param {number} [minLimit=0] - The minimum limit of the value range. Default is 0.
 * @returns {string} A CSS-compatible RGB color string, representing the color based on the value's proximity to the limits.
 */
export function getTextScaleColor(
    value,
    maxLimit,
    ascending = true,
    minLimit = 0
) {
    let red = Object.freeze({ min: 129, max: 255, range: 255 - 129 });
    let green = Object.freeze({ min: 30, max: 212, range: 212 - 30 });
    let blue = Object.freeze({ min: 26, max: 60, range: 60 - 26 });

    //Calculating percent of value from min-max range.
    let valuePercent = ((value - minLimit) / (maxLimit - minLimit)) * 100;
    valuePercent =
        valuePercent < 0
            ? 0
            : valuePercent > 100 || maxLimit === minLimit
            ? 100
            : valuePercent;

    // Calculating 2% of each color range and multiplying it with the valuePercent based on ascending boolean
    //The 2% raise allows each color to reach 100% of range when valuePercent reaches 50% of its range
    let redValue =
        (red.range / 100) * 2 * (ascending ? valuePercent : valuePercent - 50);
    let greenValue =
        (green.range / 100) *
        2 *
        (ascending ? valuePercent - 50 : valuePercent);
    //Blue value is calculated by 1% raise
    let blueValue = (blue.range / 100) * valuePercent;

    // Calculating total color value based on ascending boolean
    let redTotal = ascending ? red.min + redValue : red.max - redValue;
    let greenTotal = ascending
        ? green.max - greenValue
        : green.min + greenValue;
    let blueTotal = ascending ? blue.min + blueValue : blue.max - blueValue;

    // Limiting color values to their original min/max limits
    redTotal =
        redTotal < red.min ? red.min : redTotal > red.max ? red.max : redTotal;
    greenTotal =
        greenTotal < green.min
            ? green.min
            : greenTotal > green.max
            ? green.max
            : greenTotal;
    blueTotal =
        blueTotal < blue.min
            ? blue.min
            : blueTotal > blue.max
            ? blue.max
            : blueTotal;

    return `rgb(${redTotal},${greenTotal},${blueTotal})`;
}

export function roundToTwo(num) {
    return +(Math.round(num + "e+2") + "e-2");
}

export function normalizeString(str) {
    const map = {
        ă: "a",
        â: "a",
        î: "i",
        ș: "s",
        ț: "t",
        ȃ: "a",
        Ă: "A",
        Â: "A",
        Î: "I",
        Ș: "S",
        Ț: "T",
        Ȃ: "A",
    };
    return str.replace(/ă|â|î|ș|ț|ȃ|Ă|Â|Î|Ș|Ț|Ȃ/g, match => map[match]);
}

export function getProductSearchScore(productName, productCode, searchTerm) {
    const normalizedSearchTerm = normalizeString(
        String(searchTerm || "").toLowerCase().trim()
    );
    if (!normalizedSearchTerm) {
        return 0;
    }

    const normalizedItemName = normalizeString(
        String(productName || "").toLowerCase()
    );
    const normalizedProductCode = String(productCode || "").toLowerCase();

    let score = 0;
    const nameIndex = normalizedItemName.indexOf(normalizedSearchTerm);
    const partNoIndex = normalizedProductCode.indexOf(normalizedSearchTerm);
    if (nameIndex !== -1) {
        score += 1000 - nameIndex;
    }
    if (partNoIndex !== -1) {
        score += 500 - partNoIndex;
    }
    if (score > 0) {
        return score;
    }

    const keywords = normalizedSearchTerm.split(/\s+/).filter(Boolean);
    if (keywords.length <= 1) {
        return 0;
    }

    let keywordScore = 0;
    for (const keyword of keywords) {
        const keywordNameIndex = normalizedItemName.indexOf(keyword);
        const keywordCodeIndex = normalizedProductCode.indexOf(keyword);

        if (keywordNameIndex === -1 && keywordCodeIndex === -1) {
            return 0;
        }

        if (keywordNameIndex !== -1) {
            keywordScore += 1000 - keywordNameIndex;
        } else {
            keywordScore += 500 - keywordCodeIndex;
        }
    }

    return keywordScore;
}

/**
 * Sorts products by their product code. This method can handle both segmented (e.g., "106.01.06")
 * and non-segmented (e.g., "85060604") product codes. If both codes are non-segmented, they are compared
 * as strings. If both are segmented, it compares each segment numerically. If one code is segmented and the
 * other is not, segmented codes are either prioritized first or last based on the sort direction.
 *
 * @param {Object} a - The first product object to compare.
 * @param {Object} b - The second product object to compare.
 * @param {number} direction - The direction of the sort. Positive for ascending, negative for descending.
 * @returns {number} A negative number if `a` should come before `b`, a positive number if `a` should come after `b`,
 * or 0 if they are considered equal for sorting purposes.
 */
export function sortByProductCode(a, b, direction) {
    // Check if both codes are in a segmented format
    const isASegmented = a.product_code.includes(".");
    const isBSegmented = b.product_code.includes(".");

    if (!isASegmented && !isBSegmented) {
        // If both codes are non-segmented, compare them as strings
        return direction * a.product_code.localeCompare(b.product_code);
    } else if (isASegmented && isBSegmented) {
        // If both codes are segmented, proceed with the original numeric comparison
        const aParts = a.product_code.split(".").map(Number);
        const bParts = b.product_code.split(".").map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            // Compare each segment; treat missing segments as 0 for comparison
            const aPart = aParts[i] || 0;
            const bPart = bParts[i] || 0;
            if (aPart !== bPart) {
                return direction * (aPart - bPart);
            }
        }
    } else {
        // If one code is segmented and the other is not, prioritize segmented codes first or last based on direction
        return isASegmented ? -1 * direction : 1 * direction;
    }
    return 0;
}

/**
 * Retrieves the permissions based on the given role.
 *
 * @function getPermissionsByRole
 * @returns {Object} An object containing permission flags grouped by categories for the given role.
 * @param roleOrGroup
 */
export function getPermissionsByRole(roleOrGroup) {
    const permissions = {
        superuser: {
            isSuperuser: true,
            isAgent: false,
            defaultPath: "/home",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: true,
                canViewAllClientsListHeaders: true,
                canAddManualProduct: true,
                canViewPriceListsPage: true,
                canAddAnyOfferTypeInSummary: true,
            },
            plenumManagement: {
                canEditPlenumSize: true,
                canDeletePlenum: true,
                canAddPlenum: true,
                canExcludeRoom: true,
            },
            unitManagement: {
                canEditUnitFloors: true,
                canEditTubingDiameters: true,
                canAddUnitsAboveNeeded: true,
                canViewAllUnitModels: true,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: true,
                canViewRePowerConfirmedStatus: true,
                canViewRePowerAgentName: true,
                canViewAllRePowerClients: true,
                canEditRePowerContractNumber: true,
                canEditRePowerClientAgent: true,
                canEditRePowerClientRectificationNotes: true,
                canViewConnectionCertificateCheck: true,
                canGenerateClientsExport: true,
            },
            returnsManagement: {
                canViewReturnsAgents: true,
                canViewAllReturns: true,
                canSetOperatedToFalse: true,
                canSetOperatedToTrue: true,
                canSetInvoicedToFalse: true,
                canEditProductsQuantities: true,
                canEditProductsRealQuantities: true,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: true,
                canEditOperatedReturn: true,
                canEditReturnReceivedStatus: true,
                canSetReturnToInvoiced: true,
                canEditContactPerson: true,
                canEditPhoneNumber: true,
                canEditRegion: true,
                canEditCity: true,
                canEditAddress: true,
                canEditPickupDate: true,
                canEditPickupTimeStart: true,
                canEditPickupTimeEnd: true,
                canEditNotes: true,
                canEditInvoiceNumbers: true,
                canAddClient: true,
                canDeleteClient: true,
                canEditClientName: true,
                canEditIsProductQuantityCorrect: true,
                canEditProductService: true,
                canAddPackage: true,
                canDeletePackage: true,
                canEditPackageLength: true,
                canEditPackageWidth: true,
                canEditPackageHeight: true,
                canEditPackageWeight: true,
                canEditPackageType: true,
                canAddPackageImages: true,
                canRemovedPackageImages: true,
                canViewReturnHistoryLog: true,
                canDeleteReturn: true,
            },
            greenHouseSolarManagement: {
                canEditClient: true,
                canEditClientOffer: true,
                canViewAllClients: true,
                canViewContract: true,
                canViewClientInfo: true,
                canViewDocuments: true,
                canViewPhotosAndAnnex: true,
                canEditClientGoodsAnnex: true,
                canViewStatusHistory: true,
                canViewClientProducts: true,
                canEditClientProducts: true,
                canSaveClientProducts: true,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: true,
                canViewTempControlKit: true,
            },
            tabAccess: {
                canViewClientsListTab: true,
                canViewIntermediariesTab: true,
                canViewRePowerTab: true,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: true,
            },
            priceListManagement: {
                canViewPriceListPage: true,
                canManagePriceList: true,
            },
            intermediaries: {
                canViewAllIntermediaries: true,
            },
        },
        technicalUser: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/home",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: true,
                canViewAllClientsListHeaders: true,
                canAddManualProduct: true,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: true,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: true,
                canDeletePlenum: true,
                canAddPlenum: true,
                canExcludeRoom: true,
            },
            unitManagement: {
                canEditUnitFloors: true,
                canEditTubingDiameters: true,
                canAddUnitsAboveNeeded: true,
                canViewAllUnitModels: true,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: true,
                canViewConnectionCertificateCheck: true,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: false,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: true,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: true,
                canEditPhoneNumber: true,
                canEditRegion: true,
                canEditCity: true,
                canEditAddress: true,
                canEditPickupDate: true,
                canEditPickupTimeStart: true,
                canEditPickupTimeEnd: true,
                canEditNotes: true,
                canEditInvoiceNumbers: false,
                canAddClient: true,
                canDeleteClient: true,
                canEditClientName: true,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: true,
                canAddPackage: true,
                canDeletePackage: true,
                canEditPackageLength: true,
                canEditPackageWidth: true,
                canEditPackageHeight: true,
                canEditPackageWeight: true,
                canEditPackageType: true,
                canAddPackageImages: true,
                canRemovedPackageImages: true,
                canViewReturnHistoryLog: false,
                canDeleteReturn: true,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: false,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: false,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: false,
                canViewClientProducts: false,
                canEditClientProducts: false,
                canSaveClientProducts: false,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: true,
                canViewTempControlKit: true,

            },
            tabAccess: {
                canViewClientsListTab: true,
                canViewIntermediariesTab: true,
                canViewRePowerTab: false,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: false,
            },
            priceListManagement: {
                canViewPriceListPage: true,
                canManagePriceList: true,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        agent: {
            isSuperuser: false,
            isAgent: true,
            defaultPath: "/home",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: true,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: true,
            },
            plenumManagement: {
                canEditPlenumSize: true,
                canDeletePlenum: true,
                canAddPlenum: true,
                canExcludeRoom: true,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: true,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: true,
                canViewConnectionCertificateCheck: true,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: true,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: true,
                canEditPhoneNumber: true,
                canEditRegion: true,
                canEditCity: true,
                canEditAddress: true,
                canEditPickupDate: true,
                canEditPickupTimeStart: true,
                canEditPickupTimeEnd: true,
                canEditNotes: true,
                canEditInvoiceNumbers: false,
                canAddClient: true,
                canDeleteClient: true,
                canEditClientName: true,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: true,
                canAddPackage: true,
                canDeletePackage: true,
                canEditPackageLength: true,
                canEditPackageWidth: true,
                canEditPackageHeight: true,
                canEditPackageWeight: true,
                canEditPackageType: true,
                canAddPackageImages: true,
                canRemovedPackageImages: true,
                canViewReturnHistoryLog: false,
                canDeleteReturn: true,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: true,
                canViewAllClients: false,
                canViewContract: true,
                canViewClientInfo: true,
                canViewDocuments: true,
                canViewPhotosAndAnnex: true,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: true,
                canViewClientProducts: true,
                canEditClientProducts: false,
                canSaveClientProducts: false,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: true,
                canViewIntermediariesTab: true,
                canViewRePowerTab: false,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: true,
            },
            priceListManagement: {
                canViewPriceListPage: true,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        superAgent: {
            isSuperuser: false,
            isAgent: true,
            defaultPath: "/home",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: true,
                canViewAllClientsListHeaders: true,
                canAddManualProduct: false,
                canViewPriceListsPage: true,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: true,
            },
            plenumManagement: {
                canEditPlenumSize: true,
                canDeletePlenum: true,
                canAddPlenum: true,
                canExcludeRoom: true,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: true,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: true,
                canViewConnectionCertificateCheck: true,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: true,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: true,
                canEditPhoneNumber: true,
                canEditRegion: true,
                canEditCity: true,
                canEditAddress: true,
                canEditPickupDate: true,
                canEditPickupTimeStart: true,
                canEditPickupTimeEnd: true,
                canEditNotes: true,
                canEditInvoiceNumbers: false,
                canAddClient: true,
                canDeleteClient: true,
                canEditClientName: true,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: true,
                canAddPackage: true,
                canDeletePackage: true,
                canEditPackageLength: true,
                canEditPackageWidth: true,
                canEditPackageHeight: true,
                canEditPackageWeight: true,
                canEditPackageType: true,
                canAddPackageImages: true,
                canRemovedPackageImages: true,
                canViewReturnHistoryLog: false,
                canDeleteReturn: true,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: true,
                canViewAllClients: false,
                canViewContract: true,
                canViewClientInfo: true,
                canViewDocuments: true,
                canViewPhotosAndAnnex: true,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: true,
                canViewClientProducts: true,
                canEditClientProducts: false,
                canSaveClientProducts: true,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: true,
                canViewIntermediariesTab: true,
                canViewRePowerTab: false,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: true,
            },
            priceListManagement: {
                canViewPriceListPage: true,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        clientFormRegionOptional: {
            isSuperuser: false,
            isAgent: true,
            defaultPath: "/home",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canRequireRegionOnClientForm: true,
            },
            tabAccess: {
                canViewClientsListTab: true,
                canViewIntermediariesTab: true,
                canViewRePowerTab: false,
                canViewReturnsTab: true,

                canViewGreenHouseSolarClientsTab: false,
            },
        },
        // Additive-only: merges on top of existing groups without pulling agent tabs.
        priceListVerifier: {
            priceListManagement: {
                canViewPriceListPage: true,
                canManagePriceList: false,
                canVerifyPriceListProducts: true,
            },
        },
        greenHouseOnly: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/clients-precontracts",
            general: {
                canViewLiveApp: false,
                canFetchAllData: false,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: false,
                canViewConnectionCertificateCheck: false,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: false,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: false,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: false,
                canEditPhoneNumber: false,
                canEditRegion: false,
                canEditCity: false,
                canEditAddress: false,
                canEditPickupDate: false,
                canEditPickupTimeStart: false,
                canEditPickupTimeEnd: false,
                canEditNotes: false,
                canEditInvoiceNumbers: false,
                canAddClient: false,
                canDeleteClient: false,
                canEditClientName: false,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: false,
                canAddPackage: false,
                canDeletePackage: false,
                canEditPackageLength: false,
                canEditPackageWidth: false,
                canEditPackageHeight: false,
                canEditPackageWeight: false,
                canEditPackageType: false,
                canAddPackageImages: false,
                canRemovedPackageImages: false,
                canViewReturnHistoryLog: false,
                canDeleteReturn: false,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: false,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: false,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: false,
                canViewClientProducts: false,
                canEditClientProducts: false,
                canSaveClientProducts: false,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: false,
                canViewGreenHouseSolarClientsTab: false,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        repowerAndGreenHouseSolarSuperUser: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/clients-precontracts",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: true,
                canViewRePowerConfirmedStatus: true,
                canViewRePowerAgentName: true,
                canViewAllRePowerClients: true,
                canEditRePowerContractNumber: true,
                canEditRePowerClientAgent: true,
                canEditRePowerClientRectificationNotes: true,
                canViewConnectionCertificateCheck: true,
                canGenerateClientsExport: true,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: false,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: false,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: false,
                canEditPhoneNumber: false,
                canEditRegion: false,
                canEditCity: false,
                canEditAddress: false,
                canEditPickupDate: false,
                canEditPickupTimeStart: false,
                canEditPickupTimeEnd: false,
                canEditNotes: false,
                canEditInvoiceNumbers: false,
                canAddClient: false,
                canDeleteClient: false,
                canEditClientName: false,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: false,
                canAddPackage: false,
                canDeletePackage: false,
                canEditPackageLength: false,
                canEditPackageWidth: false,
                canEditPackageHeight: false,
                canEditPackageWeight: false,
                canEditPackageType: false,
                canAddPackageImages: false,
                canRemovedPackageImages: false,
                canViewReturnHistoryLog: false,
                canDeleteReturn: false,
            },
            greenHouseSolarManagement: {
                canEditClient: true,
                canEditClientOffer: true,
                canViewAllClients: true,
                canViewContract: true,
                canViewClientInfo: true,
                canViewDocuments: true,
                canViewPhotosAndAnnex: true,
                canEditClientGoodsAnnex: true,
                canViewStatusHistory: true,
                canViewClientProducts: true,
                canEditClientProducts: true,
                canSaveClientProducts: true,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: false,
                canViewGreenHouseSolarClientsTab: true,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        repowerSuperUser: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/clients-precontracts",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: true,
                canViewRePowerConfirmedStatus: true,
                canViewRePowerAgentName: true,
                canViewAllRePowerClients: true,
                canEditRePowerContractNumber: true,
                canEditRePowerClientAgent: true,
                canEditRePowerClientRectificationNotes: true,
                canViewConnectionCertificateCheck: true,
                canGenerateClientsExport: true,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: false,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: false,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: false,
                canEditPhoneNumber: false,
                canEditRegion: false,
                canEditCity: false,
                canEditAddress: false,
                canEditPickupDate: false,
                canEditPickupTimeStart: false,
                canEditPickupTimeEnd: false,
                canEditNotes: false,
                canEditInvoiceNumbers: false,
                canAddClient: false,
                canDeleteClient: false,
                canEditClientName: false,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: false,
                canAddPackage: false,
                canDeletePackage: false,
                canEditPackageLength: false,
                canEditPackageWidth: false,
                canEditPackageHeight: false,
                canEditPackageWeight: false,
                canEditPackageType: false,
                canAddPackageImages: false,
                canRemovedPackageImages: false,
                canViewReturnHistoryLog: false,
                canDeleteReturn: false,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: false,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: false,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: false,
                canViewClientProducts: false,
                canEditClientProducts: false,
                canSaveClientProducts: false,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: true,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: true,
                canViewReturnsTab: false,
                canViewGreenHouseSolarClientsTab: false,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        greenHouseSolarSuperUser: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/green-house-client-solar-tab",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: false,
                canViewConnectionCertificateCheck: false,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: false,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: false,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: false,
                canEditPhoneNumber: false,
                canEditRegion: false,
                canEditCity: false,
                canEditAddress: false,
                canEditPickupDate: false,
                canEditPickupTimeStart: false,
                canEditPickupTimeEnd: false,
                canEditNotes: false,
                canEditInvoiceNumbers: false,
                canAddClient: false,
                canDeleteClient: false,
                canEditClientName: false,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: false,
                canAddPackage: false,
                canDeletePackage: false,
                canEditPackageLength: false,
                canEditPackageWidth: false,
                canEditPackageHeight: false,
                canEditPackageWeight: false,
                canEditPackageType: false,
                canAddPackageImages: false,
                canRemovedPackageImages: false,
                canViewReturnHistoryLog: false,
                canDeleteReturn: false,
            },
            greenHouseSolarManagement: {
                canEditClient: true,
                canEditClientOffer: true,
                canViewAllClients: true,
                canViewContract: true,
                canViewClientInfo: true,
                canViewDocuments: true,
                canViewPhotosAndAnnex: true,
                canEditClientGoodsAnnex: true,
                canViewStatusHistory: true,
                canViewClientProducts: true,
                canEditClientProducts: true,
                canSaveClientProducts: true,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: false,
                canViewGreenHouseSolarClientsTab: true,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        returnServiceSuperUser: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/returns-tab",
            general: {
                canViewLiveApp: false,
                canFetchAllData: false,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: false,
                canViewConnectionCertificateCheck: false,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: true,
                canViewAllReturns: true,
                canSetOperatedToFalse: true,
                canSetOperatedToTrue: true,
                canSetInvoicedToFalse: true,
                canEditProductsQuantities: true,
                canEditProductsRealQuantities: true,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: true,
                canEditOperatedReturn: true,
                canEditReturnReceivedStatus: true,
                canSetReturnToInvoiced: true,
                canEditContactPerson: true,
                canEditPhoneNumber: true,
                canEditRegion: true,
                canEditCity: true,
                canEditAddress: true,
                canEditPickupDate: true,
                canEditPickupTimeStart: true,
                canEditPickupTimeEnd: true,
                canEditNotes: true,
                canEditInvoiceNumbers: true,
                canAddClient: true,
                canDeleteClient: true,
                canEditClientName: true,
                canEditIsProductQuantityCorrect: true,
                canEditProductService: true,
                canAddPackage: true,
                canDeletePackage: true,
                canEditPackageLength: true,
                canEditPackageWidth: true,
                canEditPackageHeight: true,
                canEditPackageWeight: true,
                canEditPackageType: true,
                canAddPackageImages: true,
                canRemovedPackageImages: true,
                canViewReturnHistoryLog: true,
                canDeleteReturn: true,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: false,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: false,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: false,
                canViewClientProducts: false,
                canEditClientProducts: false,
                canSaveClientProducts: false,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: false,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        acquisitionsOnly: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/returns-tab",
            general: {
                canViewLiveApp: false,
                canFetchAllData: false,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: false,
                canViewConnectionCertificateCheck: false,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: true,
                canViewAllReturns: true,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: false,
                canEditProductsRealQuantities: true,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: true,
                canEditReturnReceivedStatus: true,
                canSetReturnToInvoiced: false,
                canEditContactPerson: false,
                canEditPhoneNumber: false,
                canEditRegion: false,
                canEditCity: false,
                canEditAddress: false,
                canEditPickupDate: false,
                canEditPickupTimeStart: false,
                canEditPickupTimeEnd: false,
                canEditNotes: false,
                canEditInvoiceNumbers: false,
                canAddClient: false,
                canDeleteClient: false,
                canEditClientName: false,
                canEditIsProductQuantityCorrect: true,
                canEditProductService: false,
                canAddPackage: false,
                canDeletePackage: false,
                canEditPackageLength: false,
                canEditPackageWidth: false,
                canEditPackageHeight: false,
                canEditPackageWeight: false,
                canEditPackageType: false,
                canAddPackageImages: true,
                canRemovedPackageImages: false,
                canViewReturnHistoryLog: false,
                canDeleteReturn: false,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: true,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: true,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: true,
                canViewStatusHistory: false,
                canViewClientProducts: true,
                canEditClientProducts: true,
                canSaveClientProducts: true,
                canViewClientIdentity: true,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: true,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        logisticsOnly: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/returns-tab",
            general: {
                canViewLiveApp: false,
                canFetchAllData: false,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: false,
                canViewConnectionCertificateCheck: false,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: true,
                canViewAllReturns: true,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: true,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: false,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: true,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: false,
                canEditPhoneNumber: false,
                canEditRegion: false,
                canEditCity: false,
                canEditAddress: false,
                canEditPickupDate: false,
                canEditPickupTimeStart: false,
                canEditPickupTimeEnd: false,
                canEditNotes: false,
                canEditInvoiceNumbers: false,
                canAddClient: false,
                canDeleteClient: false,
                canEditClientName: false,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: false,
                canAddPackage: false,
                canDeletePackage: false,
                canEditPackageLength: false,
                canEditPackageWidth: false,
                canEditPackageHeight: false,
                canEditPackageWeight: false,
                canEditPackageType: false,
                canAddPackageImages: false,
                canRemovedPackageImages: false,
                canViewReturnHistoryLog: false,
                canDeleteReturn: false,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: false,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: false,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: false,
                canViewClientProducts: false,
                canEditClientProducts: false,
                canSaveClientProducts: false,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: false,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        ordersOnly: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/returns-tab",
            general: {
                canViewLiveApp: true,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: false,
                canViewConnectionCertificateCheck: false,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: true,
                canViewAllReturns: true,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: false,
                canViewReturnCreationDate: true,
                canEditProductsRealQuantities: false,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: true,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: true,
                canEditContactPerson: false,
                canEditPhoneNumber: false,
                canEditRegion: false,
                canEditCity: false,
                canEditAddress: false,
                canEditPickupDate: false,
                canEditPickupTimeStart: false,
                canEditPickupTimeEnd: false,
                canEditNotes: false,
                canEditInvoiceNumbers: true,
                canAddClient: false,
                canDeleteClient: false,
                canEditClientName: false,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: false,
                canAddPackage: false,
                canDeletePackage: false,
                canEditPackageLength: false,
                canEditPackageWidth: false,
                canEditPackageHeight: false,
                canEditPackageWeight: false,
                canEditPackageType: false,
                canAddPackageImages: false,
                canRemovedPackageImages: false,
                canViewReturnHistoryLog: false,
                canDeleteReturn: false,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: false,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: false,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: false,
                canViewClientProducts: false,
                canEditClientProducts: false,
                canSaveClientProducts: false,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: false,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        returnsOperator: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/returns-tab",
            general: {
                canViewLiveApp: false,
                canFetchAllData: false,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: false,
                canViewConnectionCertificateCheck: false,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: true,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: true,
                canEditPhoneNumber: true,
                canEditRegion: true,
                canEditCity: true,
                canEditAddress: true,
                canEditPickupDate: true,
                canEditPickupTimeStart: true,
                canEditPickupTimeEnd: true,
                canEditNotes: true,
                canEditInvoiceNumbers: false,
                canAddClient: true,
                canDeleteClient: true,
                canEditClientName: true,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: true,
                canAddPackage: true,
                canDeletePackage: true,
                canEditPackageLength: true,
                canEditPackageWidth: true,
                canEditPackageHeight: true,
                canEditPackageWeight: true,
                canEditPackageType: true,
                canAddPackageImages: true,
                canRemovedPackageImages: true,
                canViewReturnHistoryLog: false,
                canDeleteReturn: true,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: false,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: false,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: false,
                canViewClientProducts: false,
                canEditClientProducts: false,
                canSaveClientProducts: false,
                canViewClientIdentity: false,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: true,
                canViewGreenHouseSolarClientsTab: false,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
        greenHouseSolarClientProductsOnly: {
            isSuperuser: false,
            isAgent: false,
            defaultPath: "/green-house-client-solar-tab",
            general: {
                canViewLiveApp: false,
                canFetchAllData: true,
            },
            clientManagement: {
                canEditClientAgent: false,
                canViewAllClientsListHeaders: false,
                canAddManualProduct: false,
                canViewPriceListsPage: false,
                canAddAnyOfferTypeInSummary: false,
                canViewHimselfAsAgent: false,
            },
            plenumManagement: {
                canEditPlenumSize: false,
                canDeletePlenum: false,
                canAddPlenum: false,
                canExcludeRoom: false,
            },
            unitManagement: {
                canEditUnitFloors: false,
                canEditTubingDiameters: false,
                canAddUnitsAboveNeeded: false,
                canViewAllUnitModels: false,
            },
            rePowerManagement: {
                canViewRePowerAgentFilter: false,
                canViewRePowerConfirmedStatus: false,
                canViewRePowerAgentName: false,
                canViewAllRePowerClients: false,
                canEditRePowerContractNumber: false,
                canEditRePowerClientAgent: false,
                canEditRePowerClientRectificationNotes: false,
                canViewConnectionCertificateCheck: false,
                canGenerateClientsExport: false,
            },
            returnsManagement: {
                canViewReturnsAgents: false,
                canViewAllReturns: false,
                canSetOperatedToFalse: false,
                canSetOperatedToTrue: false,
                canSetInvoicedToFalse: false,
                canEditProductsQuantities: true,
                canEditProductsRealQuantities: false,
                canViewReturnCreationDate: true,
                canViewReturnTransportationCost: false,
                canEditOperatedReturn: false,
                canEditReturnReceivedStatus: false,
                canSetReturnToInvoiced: false,
                canEditContactPerson: true,
                canEditPhoneNumber: true,
                canEditRegion: true,
                canEditCity: true,
                canEditAddress: true,
                canEditPickupDate: true,
                canEditPickupTimeStart: true,
                canEditPickupTimeEnd: true,
                canEditNotes: true,
                canEditInvoiceNumbers: false,
                canAddClient: true,
                canDeleteClient: true,
                canEditClientName: true,
                canEditIsProductQuantityCorrect: false,
                canEditProductService: true,
                canAddPackage: true,
                canDeletePackage: true,
                canEditPackageLength: true,
                canEditPackageWidth: true,
                canEditPackageHeight: true,
                canEditPackageWeight: true,
                canEditPackageType: true,
                canAddPackageImages: true,
                canRemovedPackageImages: true,
                canViewReturnHistoryLog: false,
                canDeleteReturn: true,
            },
            greenHouseSolarManagement: {
                canEditClient: false,
                canEditClientOffer: false,
                canViewAllClients: true,
                canViewContract: false,
                canViewClientInfo: false,
                canViewDocuments: false,
                canViewPhotosAndAnnex: false,
                canEditClientGoodsAnnex: false,
                canViewStatusHistory: false,
                canViewClientProducts: true,
                canEditClientProducts: true,
                canSaveClientProducts: true,
                canViewClientIdentity: true,
            },
            ipaOfferManagement: {
                canViewRRDistribuitor: false,
                canViewTempControlKit: false,
            },
            tabAccess: {
                canViewClientsListTab: false,
                canViewIntermediariesTab: false,
                canViewRePowerTab: false,
                canViewReturnsTab: false,
                canViewGreenHouseSolarClientsTab: true,
            },
            priceListManagement: {
                canViewPriceListPage: false,
                canManagePriceList: false,
            },
            intermediaries: {
                canViewAllIntermediaries: false,
            },
        },
    };

    return permissions[roleOrGroup] || {};
}

export function mergePermissions(groupPermissions) {
    const merged = {};

    groupPermissions.forEach(groupPermission => {
        deepMerge(merged, groupPermission);
    });

    return merged;
}

function deepMerge(target, source) {
    for (const key in source) {
        // If source[key] is an object (and not an array), recurse into it.
        if (
            source[key] &&
            typeof source[key] === "object" &&
            !Array.isArray(source[key])
        ) {
            // Ensure target[key] is an object
            if (!target[key] || typeof target[key] !== "object") {
                target[key] = {};
            }
            // Recursively merge subfields
            deepMerge(target[key], source[key]);
        } else {
            // For booleans, do logical OR
            if (typeof source[key] === "boolean") {
                target[key] = !!target[key] || source[key];
            } else {
                // Otherwise just overwrite, or handle differently if needed
                target[key] = source[key];
            }
        }
    }
}

/**
 * priceListVerifier restricts only price-list management, even for superuser /
 * technicalUser. Other permission areas stay unchanged.
 *
 * @param {Object} permissions
 * @param {string[]} [groups]
 * @returns {Object}
 */
function applyPriceListVerifierOverride(permissions, groups = []) {
    const groupNames = Array.isArray(groups) ? groups : [];
    if (!permissions || !groupNames.includes("priceListVerifier")) {
        return permissions;
    }
    return {
        ...permissions,
        priceListManagement: {
            ...(permissions.priceListManagement || {}),
            canViewPriceListPage: true,
            canManagePriceList: false,
            canVerifyPriceListProducts: true,
        },
    };
}

/**
 * Sets the user's permissions in the Vuex store based on the provided role.
 * Dispatches a Vuex action to set the permissions for the specified role.
 *
 * @function setUserPermissionsByRole
 * @param {Object} context - The Vue component context (usually `this`) for dispatching Vuex actions.
 * @param {string} role - The role of the user (e.g., 'superUser', 'technicalUser', 'superAgent', 'agent').
 * @param groups
 */
export function setUserPermissionsByRole(context, role, groups = []) {
    let resolvedRole = role;
    let permissions;

    if (role === "superuser") {
        resolvedRole = "superuser";
        permissions = getPermissionsByRole("superuser");
    } else if (role === "technicalUser") {
        resolvedRole = "technicalUser";
        permissions = getPermissionsByRole("technicalUser");
    } else if (role === "superAgent") {
        resolvedRole = "superAgent";
        const hasGroups = Array.isArray(groups) && groups.length > 0;
        const baseSuperAgentPermissions = getPermissionsByRole("superAgent");
        permissions = hasGroups
            ? mergePermissions([
                  baseSuperAgentPermissions,
                  ...groups.map((group) => getPermissionsByRole(group)),
              ])
            : baseSuperAgentPermissions;
    } else if (role === "groupBased") {
        if (!groups) {
            return;
        }
        resolvedRole = groups.join(", "); // Combine groups for debugging
        const groupPermissions = groups.map((group) => {
            const groupPermission = getPermissionsByRole(group);

            // This group is an additive "agent variant" and should preserve
            // all default agent capabilities (tabs, save/view rights), while
            // only adding custom behavior like region requirement handling.
            if (group === "clientFormRegionOptional") {
                return mergePermissions([
                    getPermissionsByRole("agent"),
                    groupPermission,
                ]);
            }

            // priceListVerifier is additive-only (price list flags). Do not
            // merge agent here — that would OR clients/intermediaries tabs
            // onto restricted groups like ordersOnly.
            return groupPermission;
        });
        permissions = mergePermissions(groupPermissions);

        // Verifier-only accounts still need an agent baseline for the rest of the app.
        const hasNonAdditiveGroup = groups.some(
            (group) => group !== "priceListVerifier" && group !== "clientFormRegionOptional",
        );
        if (groups.includes("priceListVerifier") && !hasNonAdditiveGroup) {
            permissions = mergePermissions([
                getPermissionsByRole("agent"),
                permissions,
            ]);
        }
    } else {
        resolvedRole = "agent";
        permissions = getPermissionsByRole("agent");
    }

    context.dispatch("setUserPermissions", {
        role: resolvedRole,
        permissions: applyPriceListVerifierOverride(permissions, groups),
    });
}

/**
 * Updates the "last seen" version for the user.
 * Sends a POST request to update the user's last seen version.
 * If the request fails, an error modal is shown.
 *
 * @async
 * @function updateLastSeenUpdate
 * @param {Object} context - The Vue component context (usually `this`) for accessing Vuex store and other resources.
 * @returns {Promise<Object|undefined>} The response data if the request is successful, otherwise undefined.
 */
async function updateLastSeenUpdate(context) {
    try {
        const response = await apiClient.post("/api/update-last-seen/", {});
        return response.data;
    } catch (error) {
        if (context) {
            showAlertModal(
                context.$store,
                "A apărut o eroare la update-ul ultimei versiuni. Vă rugăm să încercați din nou.",
                "danger",
                12000,
                error
            );
        }
        console.error("Error updating last seen update:", error);
    }
}

const MAINTENANCE_BYPASS_EMAIL = "dev_admin@ametras.ro";
const MAINTENANCE_WARNING_CONFIRM_INTERVAL_MS = 300000; // 5 minutes

let maintenanceCache = {
    status: "off",
    scheduled_at: null,
    ends_at: null,
    message: "",
    bypass: false,
    updated_at: null,
    fetchedAt: 0,
};

let lastMaintenanceWarningConfirmAt = 0;
let lastSeenMaintenanceUpdatedAt = null;
let lastMaintenanceStatus = "off";

/**
 * Format a maintenance scheduled_at ISO string for Romanian locale display.
 * @param {string|null} iso
 * @returns {string}
 */
export function formatMaintenanceTime(iso) {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    // Always display Europe/Bucharest so browser OS timezone cannot shift the label
    return date.toLocaleString("ro-RO", {
        timeZone: "Europe/Bucharest",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}

export function getMaintenanceCache() {
    return maintenanceCache;
}

export function isMaintenanceBypassEmail(email) {
    return (email || "").toLowerCase() === MAINTENANCE_BYPASS_EMAIL.toLowerCase();
}

const MAINTENANCE_RELOGIN_FLAG = "maintenanceRequiresRelogin";

function buildMaintenanceWarningMessage(cache) {
    const startLabel = formatMaintenanceTime(cache.scheduled_at);
    const endLabel = formatMaintenanceTime(cache.ends_at);
    const customMessage = (cache.message || "").trim();

    let whenText = "în curând";
    if (startLabel && endLabel) {
        whenText = `între ${startLabel} și ${endLabel}`;
    } else if (startLabel) {
        whenText = `începând cu ${startLabel}`;
    }

    const defaultMessage =
        `Atenție: ${whenText} se vor efectua lucrări de mentenanță pe platformă.\n\n` +
        `Vă rugăm să salvați toate lucrările în curs. Datele nesalvate se vor pierde.\n\n` +
        `Pe durata mentenanței nu vă veți putea conecta și aplicația va fi indisponibilă. ` +
        `După finalizare va trebui să vă autentificați din nou.`;

    if (customMessage) {
        return `${defaultMessage}\n\n${customMessage}`;
    }
    return defaultMessage;
}

/**
 * Clear auth so after maintenance the user must log in again and reload fresh data.
 */
function forceLogoutForMaintenance() {
    try {
        localStorage.setItem(MAINTENANCE_RELOGIN_FLAG, "1");
    } catch (e) {
        /* ignore */
    }
    try {
        deleteAuthToken();
    } catch (e) {
        /* ignore */
    }
    try {
        localStorage.removeItem("token");
        sessionStorage.clear();
    } catch (e) {
        /* ignore */
    }
}

function redirectToLoginAfterMaintenance() {
    try {
        localStorage.removeItem(MAINTENANCE_RELOGIN_FLAG);
    } catch (e) {
        /* ignore */
    }
    const path = window.location.pathname;
    if (path === "/login" || path === "/") {
        return;
    }
    window.location.assign("/login");
}

/**
 * Fetch maintenance status. Shows warning dialog; on active forces logout + maintenance page;
 * when maintenance ends, sends user to login so they re-fetch everything.
 * @param {Object} [options]
 * @param {boolean} [options.showWarningConfirm=true]
 * @returns {Promise<Object|null>}
 */
export async function checkMaintenanceMode(options = {}) {
    const {
        showWarningConfirm = true,
    } = options;

    try {
        const response = await apiClient.get("/api/maintenance-status/");
        const data = response.data || {};
        const previousStatus = lastMaintenanceStatus;
        maintenanceCache = {
            status: data.status || "off",
            scheduled_at: data.scheduled_at || null,
            ends_at: data.ends_at || null,
            message: data.message || "",
            bypass: !!data.bypass,
            updated_at: data.updated_at || null,
            fetchedAt: Date.now(),
        };

        const localBypass =
            maintenanceCache.bypass ||
            isMaintenanceBypassEmail(sessionStorage.getItem("userEmail"));

        // Only after maintenance is fully over → force login (not during warning)
        if (maintenanceCache.status === "off") {
            lastMaintenanceStatus = "off";
            const onMaintenancePage =
                window.location.pathname === "/maintenance";
            const needsRelogin =
                localStorage.getItem(MAINTENANCE_RELOGIN_FLAG) === "1";
            if (onMaintenancePage || needsRelogin) {
                redirectToLoginAfterMaintenance();
                return maintenanceCache;
            }
            lastMaintenanceWarningConfirmAt = 0;
            lastSeenMaintenanceUpdatedAt = null;
            return maintenanceCache;
        }

        if (maintenanceCache.status === "active" && !localBypass) {
            lastMaintenanceStatus = "active";
            forceLogoutForMaintenance();
            if (window.location.pathname !== "/maintenance") {
                window.location.assign("/maintenance");
            }
            return maintenanceCache;
        }

        if (maintenanceCache.status === "active" && localBypass) {
            lastMaintenanceStatus = "active";
            lastMaintenanceWarningConfirmAt = 0;
            return maintenanceCache;
        }

        // status === warning
        if (showWarningConfirm) {
            const now = Date.now();
            const updatedAt = maintenanceCache.updated_at;
            const enteredWarning = previousStatus !== "warning";
            const isNewSave =
                !!updatedAt && updatedAt !== lastSeenMaintenanceUpdatedAt;
            const dueByInterval =
                lastMaintenanceWarningConfirmAt > 0 &&
                now - lastMaintenanceWarningConfirmAt >=
                    MAINTENANCE_WARNING_CONFIRM_INTERVAL_MS;

            if (enteredWarning || isNewSave || dueByInterval) {
                lastSeenMaintenanceUpdatedAt = updatedAt;
                lastMaintenanceWarningConfirmAt = now;
                lastMaintenanceStatus = "warning";
                emitter.emit("maintenance-warning", {
                    message: buildMaintenanceWarningMessage(maintenanceCache),
                    scheduled_at: maintenanceCache.scheduled_at,
                    ends_at: maintenanceCache.ends_at,
                });
            } else {
                lastMaintenanceStatus = "warning";
            }
        }
        // If showWarningConfirm=false (router), do NOT mark status as warning yet,
        // so the next poll with showWarningConfirm=true still counts as enteredWarning.

        return maintenanceCache;
    } catch (error) {
        console.error("Error checking maintenance mode:", error);
        return null;
    }
}

/**
 * Checks if there are any updates available for the application.
 * Sends a GET request to check for updates, and prompts the user to reload or re-login if an update is required.
 * - For a minor update, the user is asked to reload the page.
 * - For a major update, the user is required to log in again.
 *
 * @async
 * @function checkForUpdates
 * @param {Object} context - The Vue component context (usually `this`) for accessing Vuex store and other resources.
 * @returns {Promise<void>} No return value. The function handles reloading the page or redirecting based on the update type.
 */
export async function checkForUpdates(context) {
    try {
        // Check if 'isClientUser' exists and is set to 'true' in sessionStorage
        const isClientUser = sessionStorage.getItem("isClientUser") === "true";
        if (isClientUser) {
            return; // Exit the function early if the user is a client user
        }

        // const response = await apiClient.get("/api/check-for-updates/");

        const token = localStorage.getItem("apiToken"); // wherever you stored it
        const response = await apiClient.get("/api/check-for-updates/", {
            headers: token ? { Authorization: `Token ${token}` } : {},
        });

        const data = response.data;

        if (data.update_needed) {
            if (data.update_type === "minor") {
                const updateNotification = confirm(
                    "Versiune nouă disponibilă. Vă rugăm reîncărcați pagina."
                );
                if (updateNotification) {
                    await updateLastSeenUpdate(context).then(() => {
                        window.location.reload();
                    });
                }
            } else if (data.update_type === "major") {
                const reLoginNotification = confirm(
                    "Update important disponibil. Vă rugăm sa vă relogați pentru a realiza update-ul."
                );
                if (reLoginNotification) {
                    await updateLastSeenUpdate(context).then(() => {
                        window.location.href = "/login";
                    });
                    deleteAuthToken();
                    localStorage.clear();
                    sessionStorage.clear();
                }
            }
        }
    } catch (error) {
        if (context) {
            showAlertModal(
                context.$store,
                "A apărut o eroare la verificarea ultimelor update-uri. Vă rugăm să încercați din nou.",
                "danger",
                12000,
                error
            );
        }
        console.error("Error checking for updates:", error);
    }
}

export async function fetchRegions() {
    try {
        const response = await fetch("/cities.json");
        const data = await response.json();
        let regions = data
            .filter(item => item.model === "cities_light.region")
            .sort((a, b) => a.fields.name.localeCompare(b.fields.name))
            .map(item => ({
                title: item.fields.name,
                value: item.pk,
            }));

        sessionStorage.setItem("regions", JSON.stringify(regions));

        return regions;
    } catch (error) {
        console.error("Error fetching regions:", error);
        return [];
    }
}

/**
 * Fetches and processes subregion data from a local JSON file, normalizing names by removing certain prefixes.
 * Caches the result in sessionStorage for future use.
 * Logs an error if data cannot be fetched or processed.
 */
export async function fetchSubregions() {
    try {
        const prefixesToRemove = [
            "Comuna",
            "Oraș",
            "Municipiul",
            "Oras",
            "Oraş",
        ];
        const response = await fetch("/cities.json");
        const data = await response.json();
        const subregions = data
            .filter(item => item.model === "cities_light.subregion")
            .map(item => {
                let { name } = item.fields;
                prefixesToRemove.forEach(prefix => {
                    const regex = new RegExp(`^${prefix}\\s+`, "i");
                    name = name.replace(regex, "");
                });
                return {
                    title: name,
                    value: item.pk,
                    region: item.fields.region,
                };
            })
            .sort((a, b) => a.title.localeCompare(b.title));
        sessionStorage.setItem("subregions", JSON.stringify(subregions));
        return subregions;
    } catch (error) {
        console.error("Error fetching subregions:", error);
    }
}

/**
 * Fetches and processes both regions and subregions from cities.json.
 *
 * @returns {Promise<{ regions: Array, subregions: Array }>}
 */
export async function loadRegionsAndSubregions() {
    const response = await fetch("/cities.json");
    const data = await response.json();

    // or adapt to your sorting / structure needs
    const regions = data
        .filter(item => item.model === "cities_light.region")
        .map(item => ({
            id: item.pk,
            name: item.fields.name,
            display_name: item.fields.display_name,
        }));

    const subregions = data
        .filter(item => item.model === "cities_light.subregion")
        .map(item => ({
            id: item.pk,
            name: item.fields.name,
            display_name: item.fields.display_name,
            region_id: item.fields.region,
        }));

    return { regions, subregions };
}

/**
 * Finds a region's name in the given `regions` array by ID.
 *
 * @param {Array} regions - An array of region objects, e.g. [{ id: 1, name: '...' }, ...].
 * @param {number} regionId - The region ID you want to find.
 * @returns {string} - The region name if found, otherwise an empty string.
 */
export function getRegionName(regions, regionId) {
    const region = regions.find(r => r.id === regionId || r.value === regionId);
    return region ? region.name || region.title : "";
}

/**
 * Finds a subregion's name in the given `subregions` array by ID.
 *
 * @param {Array} subregions - An array of subregion objects, e.g. [{ id: 1, name: '...' }, ...].
 * @param {number} subregionId - The subregion ID you want to find.
 * @returns {string} - The subregion name if found, otherwise an empty string.
 */
export function getSubregionName(subregions, subregionId) {
    const sr = subregions.find(
        s => s.id === subregionId || s.value === subregionId
    );
    return sr ? sr.name || sr.title : "";
}

/**
 * Sets default session values, including client types, for use across the application.
 * Saves the client types to session storage to standardize dropdowns or other UI components.
 */
export function setDefaultValues() {
    //Set default values:
    let clientTypes = [
        { value: 1, text: "Client Final" },
        { value: 2, text: "Instalator" },
        { value: 3, text: "Constructor" },
        { value: 4, text: "Dezvoltator" },
        { value: 5, text: "Magazin" },
        { value: 6, text: "Agent" },
        { value: 7, text: "Arhitect" },
        { value: 8, text: "Proiectant" },
        { value: 9, text: "Designer" },
    ];
    sessionStorage.setItem("clientTypes", JSON.stringify(clientTypes));
}

/**
 * Send delivery-related notification email to the relevant parties.
 *
 * @function sendDeliveryEmail
 * @param {Object} params - Parameters for the email
 * @param {string} params.scenario - One of: admin_approved, agent_partial, admin_rejected, etc.
 * @param {number} params.client_id - ID of the client involved
 * @param {string} params.actor - Who is triggering the email ("admin", "agent")
 * @param {string} [params.delivery_date] - Optional delivery date in ISO format.
 * @param {boolean} [params.test=false] - Whether to simulate the email sending without actually delivering it
 * @returns {Promise<void>} Resolves when the request completes
 */
export async function sendDeliveryEmail({
    scenario,
    client_id,
    actor,
    delivery_date,
    description,
    partial_delivery = false,
    test = false,
}) {
    try {
        await apiClient.post("/api/send-delivery-email/", {
            scenario,
            client_id,
            actor,
            delivery_date,
            description,
            partial_delivery,
            test,
        });
    } catch (error) {
        console.error("Eroare la trimiterea emailului de livrare:", error);
        throw error;
    }
}

/**
 * Opens the generated Additional Act PDF for an existing act.
 * @param {number} actId - ID of the AdditionalAct instance.
 */
export function createPDFActAditional(actId) {
    const pdfLink = `${process.env.VUE_APP_API_BASE_URL}/api/pdf/generate_pdf_act_aditional/?act_id=${actId}`;
    openPDFInNewTab(pdfLink);
}
